import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  getValue,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  ANALYTICS_FALLBACK_USER_NAME,
  type AssistantForAnalytics,
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  SKILL_MD_TRAITS,
  toAssistantForAnalytics,
} from '@shared';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, firstValueFrom, map, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import * as fromGenerated from '../../../_generated';
import { type SharedWithType } from '../../../analytics/analytics.events';
import * as fromHelper from '../../../analytics/analytics.helper';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { type SupportedLanguage } from '../../../app.i18n';
import * as fromAuth from '../../../auth/store/auth.reducer';
import { ConfirmDialogService } from '../../../confirm-dialog/confirm-dialog.service';
import * as fromFavorites from '../../../favorites';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout/store/layout.reducer';
import { sentryCaptureError } from '../../../sentry.helper';
import {
  createFileUploadV2Store,
  FileUploadV2Store,
} from '../../shared/directives/file-upload-v2';
import { NEW_SKILL_DEFAULT_LOGO } from '../assistants.icon.helper';
import {
  defaultChatLockKey,
  isDefaultChatSkill,
  readApiErrorName,
} from '../default-chat-lock.helper';
import {
  accessToShareOption,
  AssistantShareSectionComponent,
  ShareVisibility,
} from '../share/assistant-share-section.component';
import {
  AssistantActions,
  AssistantBuilderModalActions,
  AssistantsActions,
} from '../store/assistants.actions';
import { AssistantsState } from '../store/assistants.reducer';
import {
  AssistantBuilderBasicFormComponent,
  AssistantBuilderBasicFormValues,
  BuilderLocale,
} from './assistant-builder-basic-form.component';
import {
  AssistantBuilderDataboxSelectorComponent,
  DataboxOption,
} from './assistant-builder-databox-selector.component';
import { AssistantBuilderFileValue } from './assistant-builder-file-upload.component';
import {
  AssistantBuilderModelSelectorComponent,
  ModelOption,
} from './assistant-builder-model-selector.component';
import {
  AssistantBuilderTraitComparisonFormComponent,
  AssistantBuilderTraitComparisonFormValues,
} from './assistant-builder-trait-comparison-form.component';
import {
  AssistantBuilderTraitComplianceFormComponent,
  AssistantBuilderTraitComplianceFormValues,
} from './assistant-builder-trait-compliance-form.component';
import { AssistantBuilderTraitCreativeFormComponent } from './assistant-builder-trait-creative-form.component';
import { AssistantBuilderTraitGenericFormComponent } from './assistant-builder-trait-generic-form.component';
import {
  type AssistantBuilderTraitGenericFilesValue,
  AssistantBuilderTraitGenericFormValues,
} from './assistant-builder-trait-generic-form.validators';
import { AssistantBuilderTraitInsightFormComponent } from './assistant-builder-trait-insight-form.component';
import {
  AssistantBuilderTypeSelectorComponent,
  TypeOption,
  TypeOptions,
} from './assistant-builder-type-selector.component';
import {
  hasRemovePromptInput,
  isComplianceConfig,
  isComplianceConfigTranslation,
} from './assistant-builder.helper';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_UPDATE_MAX_LENGTH,
  isValidTranslation,
} from './assistant-builder.validators';

export type AssistantBuilderTraitForm =
  | AssistantBuilderTraitInsightFormComponent
  | AssistantBuilderTraitGenericFormComponent
  | AssistantBuilderTraitComparisonFormComponent
  | AssistantBuilderTraitComplianceFormComponent
  | AssistantBuilderTraitCreativeFormComponent;

/** Compliance form keys synced between fr/en; excludes per-locale labels. */
const COMPLIANCE_NOT_TRANSLATED_FORM_KEYS: Exclude<
  keyof AssistantBuilderTraitComplianceFormValues,
  'referenceLabel' | 'regularLabel'
>[] = [
  // `removePromptInput` used to be here. It is now a page-level signal shared by
  // every template, so it is not a per-slot control and needs no cross-slot sync.
  'userWillUploadReferenceDocuments',
  'configurationFile',
  'files',
] as const;

/** File chip tagged with the document role it will be persisted under. */
interface RoleTaggedFile {
  id: string;
  name: string;
  role: fromGenerated.DocumentRole;
}

@Component({
  imports: [
    FormsModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.ToggleComponent,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.ModalComponent,
    TranslateModule,
    AssistantBuilderTypeSelectorComponent,
    AssistantBuilderModelSelectorComponent,
    AssistantBuilderDataboxSelectorComponent,
    AssistantBuilderBasicFormComponent,
    AssistantBuilderTraitInsightFormComponent,
    AssistantBuilderTraitGenericFormComponent,
    AssistantBuilderTraitComparisonFormComponent,
    AssistantBuilderTraitComplianceFormComponent,
    AssistantBuilderTraitCreativeFormComponent,
    AssistantShareSectionComponent,
  ],
  selector: 'squadbox-assistant-builder',
  template: `
    <sd-modal
      [isModalVisible]="true"
      [modalOptions]="modalOptions()"
      [style.--modal-content-padding]="'0px'"
      (triggerAction)="onModalAction($event)"
    >
      <!-- Body: main column + share panel (Figma 25443:52289 modal shell). The
           two-column row owns a bounded height on desktop so the main column and
           the share panel scroll independently; on mobile it flows and the modal
           body scrolls. The height targets the D1 ~80px top/bottom margins
           (modal ≈ viewport − 160px = body + header/footer chrome); pending a
           logged-in §9 pixel-parity pass to confirm/tune. -->
      <div
        class="flex flex-col tablet-landscape:flex-row overflow-y-auto tablet-landscape:overflow-hidden hide-scrollbar tablet-landscape:h-[calc(100svh-14.5rem)]"
      >
        <!-- Main column. The 64px side padding (px-16) lives HERE, on the one
             column container — not repeated per section — so the header, the
             scrollable body, and every divider share a single inset. 32px top
             (pt-8) matches the teammate builder (Figma 25443:52477). -->
        <div
          class="grow flex flex-col min-w-0 tablet-landscape:overflow-hidden px-16"
        >
          <!-- Header: title + favorite star + divider -->
          <div class="shrink-0 pt-8">
            <div class="flex items-center gap-4">
              <!-- Saved-state title = the skill's current name (D18); New shows
                     the create title. -->
              <span
                class="typo-h6 font-semibold leading-8 text-neutral-900 grow truncate"
              >
                @if (assistant(); as skill) {
                  {{ skill.name }}
                } @else {
                  {{ 'ASSISTANTS.ASSISTANT_BUILDER.TITLE_CREATE' | translate }}
                }
              </span>
              <!-- Favorite star (SB-3325 PR-4 / Figma 25309): stageable on
                     create (applied on save), instant commit on edit (D6). -->
              <button
                sdIconButton
                variant="icon"
                [color]="isFavorite() ? 'primary' : 'neutral'"
                size="xs"
                [attr.aria-label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.FAVORITE_ARIA_LABEL' | translate
                "
                [attr.aria-pressed]="isFavorite()"
                (click)="handleToggleFavorite()"
              >
                <svg
                  [sdUseUIBasicIcon]="isFavorite() ? 'star-filled' : 'star'"
                ></svg>
              </button>
            </div>
            <div class="border-t border-neutral-200 mt-8"></div>
          </div>

          <!-- Scrollable content — a flex column. Figma's pink 16px gaps
               (25443:52477) are the flex gap-4; the 24px top/bottom is py-6
               (Gap&Padding/6) and the 64px sides come from the column container.
               Each Skill Builder Divider is its own flex item carrying 8px padding
               (py-2), so content sits 16 + 8 = 24px from the line. -->
          <div
            class="grow tablet-landscape:overflow-y-auto tablet-landscape:overflow-x-hidden hide-scrollbar flex flex-col gap-4 py-6"
          >
            <!-- Type + Model visibility gate (KD-6 / SBB-03): the kept UI-only
                 ff-advanced-assistant-builder flag hides the row whenever it is
                 OFF — in CREATE and EDIT alike. trait is immutable after create,
                 so Type stays locked in EDIT ([disabled]="!!assistant()").
                 Model is editable in EDIT (SB-4074), and the update DTO sends
                 it only when the selection changed, so hiding the row on a
                 flag-off edit cannot change it. Both are flex-1 with min-w-0 so
                 they split the row 50/50 at every width, per Figma 25459:63618
                 (1280px) and 25443:52477 (1512px) — both show equal halves
                 (SBB-11: dropped the earlier max-w-[348px] cap on Type). -->
            @if (ffAdvancedAssistantBuilder()) {
              <div
                class="flex flex-col gap-4 tablet-landscape:flex-row tablet-landscape:gap-6"
              >
                <squadbox-assistant-builder-type-selector
                  class="w-full tablet-landscape:flex-1 tablet-landscape:min-w-0"
                  [disabled]="!!assistant()"
                  [selectedType]="selectedType()"
                  (selectedTypeChange)="onSelectedTypeChange($event)"
                />
                <squadbox-assistant-builder-model-selector
                  class="w-full tablet-landscape:flex-1 tablet-landscape:min-w-0"
                  [options]="availableModels()"
                  [(selectedSlug)]="selectedModelSlug"
                />
              </div>
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
            }

            <!-- Multilingual-support toggle (SB-3325 PR-4): OFF = one universal
                 locale, ON = distinct fr + en (both required). -->
            <sd-toggle
              size="sm"
              [label]="
                'ASSISTANTS.SAVE_MODAL.FORM.SUPPORT_MULTILINGUAL.LABEL'
                  | translate
              "
              [subtext]="
                'ASSISTANTS.SAVE_MODAL.FORM.SUPPORT_MULTILINGUAL.SUBTITLE'
                  | translate
              "
              [ngModel]="supportMultilingual()"
              (ngModelChange)="supportMultilingual.set($event)"
            ></sd-toggle>
            <div class="py-2">
              <div class="border-t border-neutral-200"></div>
            </div>

            <!-- Locale-aware basic form (renders per-locale rows internally) -->
            <squadbox-assistant-builder-basic-form
              #basicForm
              [locales]="activeLocales()"
              [hasIntroMessage]="true"
              [isCreateMode]="!assistant()"
              [descriptionMaxLengthByLocale]="descriptionMaxLengthByLocale()"
              [descriptionStoredValueByLocale]="
                descriptionStoredValueByLocale()
              "
            />

            <!-- Divider -->
            <div class="py-2">
              <div class="border-t border-neutral-200"></div>
            </div>
            @let type = selectedType().type;
            @switch (type) {
              @case ('insight') {
                <squadbox-assistant-builder-trait-insight-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                />
              }
              @case ('generic') {
                <squadbox-assistant-builder-trait-generic-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                />
              }
              @case ('comparison') {
                <squadbox-assistant-builder-trait-comparison-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                />
              }
              @case ('compliance') {
                <!-- Three independent compliance slots (KD-18/KD-21, spec §6):
                       und is the single-language (OFF) editing surface; fr + en
                       are the multilingual slots. To avoid repeating the shared
                       sections (Instructions / Reference documents / toggles),
                       only ONE slot renders a visible body at a time — und (OFF)
                       or fr (ON). The en slot stays mounted-but-hidden so its
                       FormGroup still drives save/validity/sync; its per-locale
                       Customize labels are rendered inline by the fr slot via
                       the secondaryForm input (Figma 11531:14046). -->
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormUnd
                  [class.hidden]="supportMultilingual()"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  [language]="currentUiLanguage()"
                />
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormFr
                  [class.hidden]="!supportMultilingual()"
                  [isMultilingual]="true"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  language="fr"
                  [secondaryForm]="complianceFormEn.form()"
                  secondaryLanguage="en"
                />
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormEn
                  class="hidden"
                  [renderCustomizeText]="false"
                  [isMultilingual]="true"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  language="en"
                />
              }
              @case ('creative') {
                <squadbox-assistant-builder-trait-creative-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [assistantForAnalytics]="assistantForAnalytics()"
                  [assistantAccessPath]="assistantAccessPath()"
                  [assistantAccessGroup]="assistantAccessGroup()"
                  [assistantTeammate]="assistantTeammate()"
                />
              }
            }

            @if (isDataboxSelectorVisible()) {
              <!-- SK-117: Databoxes multi-select, Flex (generic) trait
                     only; core-ai uses databoxes on Flex skills and the API
                     refuses them on any other trait. -->
              <squadbox-assistant-builder-databox-selector
                [options]="availableDataboxes()"
                [loadError]="databoxLoadError()"
                [(selectedIds)]="selectedDataboxIds"
              />
            }
            <!-- Shared toggle stack, directly below the knowledge-sources
                 section, in Figma 25450:27130's order: Web Search → Visual
                 analysis for PDFs → Excel file analysis → Disable message
                 input. Each row owns its OWN top divider so the stack still
                 reads correctly when a row above it is hidden (a
                 non-super_admin sees the last two, with a divider above each).
                 All four are available on every template except Basic, per
                 Charles's availability matrix in Skill Template
                 Configurations.

                 Where a row's availability is ENFORCED is a PER-ROW fact, so
                 read it per row rather than as a tally. A count here goes
                 stale the moment one row gains or loses a guard, and this
                 comment has already been wrong that way once:

                   excelFileAnalysisEnabled — trait: refused on insight by
                     validateExcelFileAnalysisEnabledForTrait. role: none, and
                     deliberately so (SB-3537 / KD-6).
                   removePromptInput — trait: refused on insight because
                     traitConfig must be {} there (IsValidTraitConfigConstraint
                     on create, validateTraitConfigForTrait against the STORED
                     trait on update). role: none.
                   webSearchEnabled, pdfVisualAnalysisEnabled — role:
                     super_admin. For the trait axis, read the validators;
                     do not infer it from this comment.

                 A row enforced on the trait axis refuses true on Basic with
                 a 400 and still accepts an explicit false, so an
                 already-enabled skill stays switchable off. Such a row also
                 gates its own payload, because the UI must not send what the
                 server refuses: buildTraitConfig returns {} on insight, and
                 the Excel create spread carries the availability check.

                 Either way the condition is expressed as "not Basic" rather
                 than an allow-list of the other four, so a new template
                 inherits the spec's default. -->
            @if (showWebSearchToggle()) {
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
              <sd-toggle
                size="sm"
                [label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.WEB_SEARCH.LABEL' | translate
                "
                [subtext]="
                  'ASSISTANTS.ASSISTANT_BUILDER.WEB_SEARCH.SUBTEXT' | translate
                "
                [ngModel]="webSearchToggleValue()"
                (ngModelChange)="webSearchEnabled.set($event)"
              ></sd-toggle>
            }

            @if (showPdfVisualAnalysisToggle()) {
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
              <sd-toggle
                size="sm"
                [label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.PDF_VISUAL_ANALYSIS.LABEL'
                    | translate
                "
                [subtext]="
                  'ASSISTANTS.ASSISTANT_BUILDER.PDF_VISUAL_ANALYSIS.SUBTEXT'
                    | translate
                "
                [ngModel]="pdfVisualAnalysisEnabled()"
                (ngModelChange)="pdfVisualAnalysisEnabled.set($event)"
              ></sd-toggle>
            }

            @if (showExcelFileAnalysisToggle()) {
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
              <sd-toggle
                size="sm"
                [label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.EXCEL_FILE_ANALYSIS.LABEL'
                    | translate
                "
                [subtext]="
                  'ASSISTANTS.ASSISTANT_BUILDER.EXCEL_FILE_ANALYSIS.SUBTEXT'
                    | translate
                "
                [ngModel]="excelFileAnalysisEnabled()"
                (ngModelChange)="excelFileAnalysisEnabled.set($event)"
              ></sd-toggle>
            }

            <!-- Disable message input. Persisted inside traitConfig (the
                 pre-existing removePromptInput key), not as its own column —
                 which is why it is bound to a plain boolean signal rather than a
                 tri-state.

                 That persistence shape says nothing about role gating, and the
                 row directly above it now proves the point: Excel file analysis
                 is its own column and carries no role gate either. Which rows
                 are role-gated is listed per row in the stack comment at the
                 top — read it there rather than counting. This one needs no
                 role gate because any builder user could
                 already set it on Compare Documents and Verify Documents
                 Compliance before it moved into the shared stack. -->
            @if (showDisableMessageInputToggle()) {
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
              <sd-toggle
                size="sm"
                [label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.DISABLE_MESSAGE_INPUT.LABEL'
                    | translate
                "
                [subtext]="
                  'ASSISTANTS.ASSISTANT_BUILDER.DISABLE_MESSAGE_INPUT.SUBTEXT'
                    | translate
                "
                [ngModel]="disableMessageInput()"
                (ngModelChange)="disableMessageInput.set($event)"
              ></sd-toggle>
            }
          </div>
        </div>

        <!-- Share panel (right on desktop, gradient surface — Figma 25307:22705).
             shrink-0 keeps it locked at w-100 (400px, per Figma) — without it,
             a long Knowledge list inflates the main column and squeezes this
             panel down to a sliver (SB-3516). -->
        @if (isShareSectionVisible()) {
          <div
            class="
                shrink-0
                p-6
                border-t border-neutral-200
                bg-gradient-to-b from-neutral-50 to-neutral-100
                tablet-landscape:border-t-0 tablet-landscape:border-l
                tablet-landscape:py-8 tablet-landscape:w-100
                tablet-landscape:overflow-y-auto hide-scrollbar
              "
          >
            <squadbox-assistant-share-section
              [allUsers]="allUsers()"
              [allGroups]="allGroups()"
              [currentUserId]="userId()?.id ?? ''"
              [owner]="shareOwner()"
              [isOrgWideEnabled]="ffShareAssistant().everyoneInMyOrganization"
              [orgWideLocked]="isDefaultChat()"
              [(shareOption)]="shareOption"
              [(selectedUserIds)]="shareSelectedUserIds"
              [(selectedGroupIds)]="shareSelectedGroupIds"
            />
          </div>
        }
      </div>
    </sd-modal>

    <!-- Confirm delete (edit only) — mirrors the assistants-list confirm modal. -->
    <sd-modal
      [isModalVisible]="isConfirmDeleteModalOpen()"
      [modalOptions]="{
        title: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.TITLE' | translate,
        showCloseButton: false,
        actions: [
          {
            label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_CANCEL' | translate,
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE' | translate,
            action: 'delete',
            type: 'delete',
            disabled: false,
          },
        ],
        backdropDismiss: true,
      }"
      (triggerAction)="handleConfirmDeleteModalAction($event)"
    >
      <div
        class="w-auto tablet-portrait:w-120"
        innerHTML="{{
          'ASSISTANTS.DELETE_ASSISTANT_MODAL.DESCRIPTION'
            | translate: { assistantName: assistant()?.name }
        }}"
      ></div>
      <p class="mt-2 w-auto tablet-portrait:w-120">
        {{ 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CONVERSATIONS' | translate }}
      </p>
    </sd-modal>
  `,
})
export class AssistantBuilderPage implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly translateStore = inject(TranslateStore);
  private readonly assistantsService = inject(fromGenerated.AssistantsService);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly assistantsStore = inject(Store<AssistantsState>);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly injector = inject(Injector);
  private readonly usersService = inject(fromGenerated.UsersService);
  private readonly groupsService = inject(fromGenerated.GroupsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  /**
   * The skill being edited, or `undefined` when creating. Fed by the assistants
   * page from the loaded entities (SB-3325 PR-4, Stage B) — the builder is a
   * modal rendered over the list, not a routed page, so there is no resolver.
   */
  public readonly assistant = input<fromGenerated.AssistantView | undefined>(
    undefined,
  );
  /**
   * `owner` calls the owner routes. `admin` calls the managed-skill routes.
   * The host reads it off the modal slice.
   */
  public readonly routeMode = input<'owner' | 'admin'>('owner');

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  /**
   * SB-3475 / KD-12. Web search is `super_admin`-only and the API enforces it
   * (`validateWebSearchEnabledForRole`), so the control is rendered on exactly the
   * same condition. Gating on the role rather than on
   * `ff-advanced-assistant-builder` is deliberate: that flag is evaluated by the
   * LaunchDarkly *browser* SDK and cannot gate anything, so rendering on it would
   * show a toggle whose save 403s.
   */
  public readonly isSuperAdmin = this.authStore.selectSignal(
    fromAuth.authFeature.selectIsSuperAdmin,
  );

  /**
   * Stored web-search state, mirroring the nullable column's three states (KD-3):
   * `null` = never set, so the skill defers to its skill.md and the payload must
   * OMIT the field. `true`/`false` = an explicit choice by this super_admin.
   * Kept as the tri-state rather than a plain boolean so an untouched toggle can
   * never write `false` over a stored value (Scenario A7).
   */
  public readonly webSearchEnabled = signal<boolean | null>(null);

  /** What the toggle renders: only an explicit `true` shows as on. */
  public readonly webSearchToggleValue = computed(
    () => this.webSearchEnabled() === true,
  );

  /**
   * Every template EXCEPT Basic (`insight`), per Charles's availability matrix
   * in Skill Template Configurations — KD-7. Deliberately expressed as "not
   * insight" rather than an allow-list of the other four, so a new template
   * type inherits the spec's default instead of silently losing the toggle.
   *
   * SERVER PAIRING, and it is lockstep (SB-3537 / KD-28,
   * https://github.com/Insyders/squadbox-app/issues/1479).
   * `validateWebSearchEnabledForTrait` in `assistants.validators.ts` is this
   * computed's server half: it 400s `true` on the four traits with no builder template, and
   * the create payload above gates on this computed so the UI never sends what
   * that guard refuses. If a product ruling widens THIS row's availability matrix
   * to Basic, the validator's refusal must move in the SAME change and BEFORE
   * this computed — widening the computed alone turns every Basic save with the
   * row on into a 400.
   *
   * That ruling does NOT exist yet, and
   * https://github.com/Insyders/squadbox-app/issues/1446 is not it: that issue
   * asks about `excelFileAnalysisEnabled` only. This row needs its own. The server deny-list is a SUPERSET of this condition: it also
   * covers `research`/`wizard`/`extract`, which the type selector cannot
   * represent, so they need no computed here.
   */
  public readonly showWebSearchToggle = computed(
    () =>
      this.isSuperAdmin() &&
      this.selectedType().type !== TypeOptions.insight.type,
  );

  /**
   * Stored "Visual analysis for PDFs" state. A plain boolean, NOT a tri-state:
   * the configuration is an enabler defaulting to OFF, so there is no third
   * "never set" state to preserve — `webSearchEnabled` above needs one only
   * because absent there means "defer to skill.md".
   *
   * The payload rules that the tri-state used to buy are expressed directly
   * instead: create omits the field unless it is `true`, and update sends it only
   * when it differs from the stored value. Both matter because the field is
   * `super_admin`-only — sending even an unchanged `false` is a 403.
   */
  public readonly pdfVisualAnalysisEnabled = signal<boolean>(false);

  /**
   * Same availability as web search, and `super_admin`-only for the same reason
   * the API restricts it: the field is currently INERT. core-ai routes PDF vision
   * per page off ingestion's own signal with no gate at all and consumes no
   * per-skill flag, so showing this to every skill creator would offer a switch
   * that does nothing. Widen this together with the wire field and a core-ai
   * consumer — the render condition deliberately mirrors
   * `validatePdfVisualAnalysisEnabledForRole`.
   *
   * Note the spec puts the configuration on four templates while core-ai's vision
   * reader is only wired into the runtimes that serve `generic` and `insight`
   * (`comparison` dispatches to its own service, as do `extract`/`compliance`).
   * So Compare Documents and Verify Documents Compliance cannot do vision at all
   * today — the KD-7 shape web search hit. Raised with Charles; not narrowed here,
   * because the spec's matrix is the source of truth for availability.
   *
   * SERVER PAIRING, and it is lockstep (SB-3537 / KD-28,
   * https://github.com/Insyders/squadbox-app/issues/1479).
   * `validatePdfVisualAnalysisEnabledForTrait` in `assistants.validators.ts` is this
   * computed's server half: it 400s `true` on the four traits with no builder template, and
   * the create payload above gates on this computed so the UI never sends what
   * that guard refuses. If a product ruling widens THIS row's availability matrix
   * to Basic, the validator's refusal must move in the SAME change and BEFORE
   * this computed — widening the computed alone turns every Basic save with the
   * row on into a 400.
   *
   * That ruling does NOT exist yet, and
   * https://github.com/Insyders/squadbox-app/issues/1446 is not it: that issue
   * asks about `excelFileAnalysisEnabled` only. This row needs its own. The server deny-list is a SUPERSET of this condition: it also
   * covers `research`/`wizard`/`extract`, which the type selector cannot
   * represent, so they need no computed here.
   *
   * READ THE "widen this together with the wire field and a core-ai consumer"
   * instruction above with that pairing in mind: followed literally it moves the
   * ROLE gate and this computed and leaves the TRAIT refusal in place, which is
   * the 400 described just above. Both halves move, or neither.
   */
  public readonly showPdfVisualAnalysisToggle = computed(
    () =>
      this.isSuperAdmin() &&
      this.selectedType().type !== TypeOptions.insight.type,
  );

  /**
   * Stored "Excel file analysis" state. Same plain-boolean shape as
   * `pdfVisualAnalysisEnabled` above — an enabler defaulting to OFF has no
   * "never set" state to preserve.
   */
  public readonly excelFileAnalysisEnabled = signal<boolean>(false);

  /**
   * Every template EXCEPT Basic (`insight`), and deliberately NOT role-gated
   * (SB-3537 / KD-6): there is no `validateExcelFileAnalysisEnabledForRole`, so
   * a role condition here would hide a toggle the server accepts. Same shape
   * and same audience as `showDisableMessageInputToggle` below — the two
   * role-gated rows above are the exception in this stack, not the rule.
   *
   * This computed also gates the CREATE payload (#1435 plan / Decision 1):
   * nothing resets the signal when `selectedType` changes, so a creator can
   * enable the row on Flex and then switch Type to Basic. The server refuses
   * that combination — `validateExcelFileAnalysisEnabledForTrait` 400s `true`
   * on `insight` — so without the gate the save fails outright rather than
   * storing a capability the builder would never render. Both reasons point the
   * same way: never send the field for a template that does not offer the row.
   *
   * That two-sided shape is NOT new here — it is the same one
   * `disableMessageInput` below already has, where `buildTraitConfig()` returns
   * `{}` on `insight` and the API rejects a non-empty `traitConfig` for Basic.
   * Which rows share it is a per-row fact, stated per row in the template
   * comment above; do not carry a tally of it here, because a tally is what
   * has already gone stale once.
   *
   * The server half is the counterpart to THIS computed, not a coincidence: if
   * Charles's open question (#1446) widens the availability matrix to Basic,
   * the trait guard for this field must move in the same change, and before
   * this condition does. Widening the computed alone turns every Basic save
   * with the row on into a 400. Any other row enforced on the trait axis
   * carries the same pairing with its own validator — check the row, not a
   * count.
   *
   * Note the field is INERT until the upload gate (#1431) and the grounded
   * answer path (#1433) land. Unlike the PDF row above, inertness is NOT a
   * reason to gate this one on a role: SB-3537 / KD-16 accepts that window
   * because a workbook produces no vectors and no Markdown, so the model
   * reports it cannot read the file rather than answering wrongly.
   */
  public readonly showExcelFileAnalysisToggle = computed(
    () => this.selectedType().type !== TypeOptions.insight.type,
  );

  /**
   * "Disable message input" (`traitConfig.removePromptInput`). Page-owned, not a
   * per-trait FormControl: the spec puts the configuration on four templates, and
   * before this it existed only inside the comparison and compliance trait forms.
   * Exactly ONE writer is important — two owners of the same persisted key means
   * whichever assembles last wins, so the controls were removed from both forms
   * rather than kept in sync.
   *
   * A plain boolean. Only `webSearchEnabled` in this stack is a tri-state, and
   * only because absent there means "defer to skill.md" — the PDF and Excel
   * rows above are plain booleans too. Here the reason is different again: this
   * value lives inside `traitConfig`, which every save sends as a full
   * overwrite, so there is no "field absent" state to preserve and nothing to
   * defer to. OFF by default.
   */
  public readonly disableMessageInput = signal<boolean>(false);

  /**
   * Every template EXCEPT Basic (`insight`), per the availability matrix — and
   * deliberately NOT role-gated. Any builder user can already set this on
   * Compare Documents and Verify Documents Compliance today, so gating it on
   * `super_admin` would be a regression. Unlike the web-search, PDF and Excel
   * rows, this one takes effect immediately: `selectPromptInputHidden` reads
   * `traitConfig.removePromptInput` with no trait branch.
   */
  public readonly showDisableMessageInputToggle = computed(
    () => this.selectedType().type !== TypeOptions.insight.type,
  );

  public readonly userId = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  /**
   * Page-owned compliance shared stores (SB-3325 PR-5): one per LOGICAL upload
   * zone (config skill.md + reference docs), bound to all three und/fr/en slots
   * so chips, upload state, and validator errors are identical and gate Save
   * cross-slot. Created imperatively — never a DI provider (lifecycle decision
   * #2: a bare `inject(FileUploadV2Store)` in the builder tree throws by
   * construction). The factory ties teardown to the page DestroyRef; the trait
   * switch + destroy effects below reset them (lifecycle decision #3).
   */
  public readonly complianceConfigStore: FileUploadV2Store =
    createFileUploadV2Store(this.injector);
  public readonly complianceReferenceStore: FileUploadV2Store =
    createFileUploadV2Store(this.injector);
  private readonly favoriteEntities = this.assistantsStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );
  /**
   * Staged favorite for the New (create) state (D6 / spec §5): the star is
   * stageable before the skill exists and applied once it is created; discarded
   * on cancel (the component is destroyed when the modal closes). On edit the
   * favorite commits instantly via the favorites store.
   */
  public readonly stagedFavorite = signal<boolean>(false);
  /** Whether the skill is favorited: the store on edit, the staged flag on create. */
  public readonly isFavorite = computed(() => {
    const skill = this.assistant();
    if (!skill) return this.stagedFavorite();
    return !!this.favoriteEntities()?.[skill.uuid];
  });

  /**
   * Compute assistant context for analytics tracking (Art IV, X).
   * Used by trait-form components to populate upload events with assistant metadata.
   */
  public readonly assistantForAnalytics = computed(() =>
    this.toAnalyticsAssistant(this.assistant()),
  );

  /**
   * The `assistantForAnalytics` derivation, taking the skill explicitly.
   *
   * Exists because the `assistant` input signal is `undefined` throughout the
   * whole synchronous create-save flow — the parent only rebinds it after
   * `loadAssistants()`, which runs *after* the reconcile that fires
   * `skill_shared`. Reading the signal there dropped the event silently; the
   * create path passes the create response instead (see `reconcileShareAccess`).
   */
  private toAnalyticsAssistant(
    skill: fromGenerated.AssistantView | undefined,
  ): AssistantForAnalytics | undefined {
    if (!skill?.uuid) return undefined;
    return toAssistantForAnalytics({
      uuid: skill.uuid,
      name: skill.translations?.[0]?.name ?? skill.uuid,
      trait: skill.trait,
      ownedBy: skill.ownedBy,
      // Already server-masked to ANALYTICS_ASSISTANT_OWNER_NAME_SQUADBOX for a
      // super_admin owner — no ownerRole/ownerFirstName/ownerLastName exist on
      // AssistantView to re-derive that mask from.
      ownerName: skill.ownerName ?? ANALYTICS_FALLBACK_USER_NAME,
    });
  }

  /** Analytics accessPath/group context for upload events (KD-3/KD-20). */
  public readonly assistantAccessPath = computed(() => {
    const skill = this.assistant();
    return skill
      ? fromHelper.toSkillAnalyticsContext(skill).accessPath
      : undefined;
  });
  public readonly assistantAccessGroup = computed(() => {
    const skill = this.assistant();
    return skill ? fromHelper.toSkillAnalyticsContext(skill).group : undefined;
  });

  /**
   * Analytics `teammate` context for the two audio upload events (KD-3).
   *
   * Deliberately NOT `toSkillAnalyticsContext(skill).teammate`, which takes an
   * unordered `teammates[0]`: the audio events use the strict N=1 collapse,
   * matching `voice_to_text_used`. The N>1 pick rule is owned by issue #1216 —
   * change both readings together when it lands.
   */
  public readonly assistantTeammate = computed(() => {
    const skill = this.assistant();
    return skill
      ? fromHelper.toSingleTeammateForAnalytics(skill.teammates)
      : undefined;
  });

  /**
   * Toggle the favorite. On edit it commits instantly (favorites store); on
   * create it only stages the choice, applied on successful create.
   */
  public handleToggleFavorite(): void {
    const skill = this.assistant();
    if (!skill) {
      this.stagedFavorite.update((staged) => !staged);
      return;
    }
    this.assistantsStore.dispatch(
      this.isFavorite()
        ? fromFavorites.AssistantFavoritesActions.removeFavorite({
            assistantId: skill.uuid,
            assistant: skill,
          })
        : fromFavorites.AssistantFavoritesActions.addFavorite({
            assistantId: skill.uuid,
            assistant: skill,
          }),
    );
  }

  public readonly fileUploadTransactionId = signal<string>('');
  public readonly selectedType = signal<TypeOption>(TypeOptions.insight);
  public readonly isSaving = signal(false);
  /**
   * The assistant uuid whose forms have already been seeded, so the assistant-
   * load effect patches once per load and never re-patches over user edits.
   */
  private patchedAssistantUuid: string | null = null;

  // Generation-model selection (SB-3310). The catalog is loaded from the
  // catalog endpoint (SB-3318, see loadModelCatalog); `selectedModelSlug` holds
  // the chosen slug and is fed into the create payload. Empty until the catalog
  // resolves — null-safe (no slug sent → deployment default).
  public readonly availableModels = signal<ModelOption[]>([]);
  public readonly selectedModelSlug = signal<string>('');
  // The slug `applyModelSelection` pre-selected: the update DTO sends `model`
  // only when the selection differs from it (SB-4074). Comparing with the
  // stored `model` instead would send the catalog default for every skill
  // whose stored model is null, pinning it on an unrelated save.
  private readonly initialModelSlug = signal<string>('');

  // Databox selection (SK-117 / SK-47). The catalog is loaded from the API
  // (loadDataboxCatalog, which proxies core-ai's GetAvailableDataboxes);
  // `selectedDataboxIds` holds the chosen ids. Flex (generic) trait only.
  // `databoxLoadError` drives the inline "unavailable" message so the rest of
  // the skill still saves on a catalog failure (SK-47).
  public readonly availableDataboxes = signal<DataboxOption[]>([]);
  public readonly databoxLoadError = signal<boolean>(false);
  public readonly selectedDataboxIds = signal<string[]>([]);
  // Seeded from the stored skill. The update DTO sends `databoxIds` only when
  // the selection differs from this (order-independent), so an unreachable
  // catalog cannot silently clear a stored selection. Seeded from
  // `assistant()`, NOT from the catalog subscribe.
  private readonly initialDataboxIds = signal<string[]>([]);

  // SK-117 review: hide the Databoxes control when the catalog is empty and
  // nothing is stored (DATABOX_DATA_SOURCE is unset in most environments, so
  // GetAvailableDataboxes returns []). Still shown when a selection is stored
  // (its ids need a removable row) or the catalog failed to load (to show the
  // unavailable message).
  public readonly isDataboxSelectorVisible = computed(
    () =>
      this.selectedType().type === TypeOptions.generic.type &&
      (this.availableDataboxes().length > 0 ||
        this.selectedDataboxIds().length > 0 ||
        this.databoxLoadError()),
  );

  // Share panel signals
  public readonly allUsers = signal<fromGenerated.UserSummaryView[]>([]);
  // Every user the caller's list returned, before the super_admin filter:
  // where the Owner row is resolved, since a super_admin caller's own row is
  // not in `allUsers`.
  private readonly directoryUsers = signal<fromGenerated.UserSummaryView[]>([]);
  public readonly allGroups = signal<fromGenerated.GroupSummaryView[]>([]);
  // Default "Only people invited" (owner-only until people are invited) — KD-23.
  public readonly shareOption = signal<ShareVisibility>('shared');
  public readonly shareSelectedUserIds = signal<string[]>([]);
  public readonly shareSelectedGroupIds = signal<string[]>([]);
  // Guards the replace-semantics reconcile: false until current access is read
  // (edit) or there is nothing to read (new). See seedShareState.
  public readonly shareAccessLoaded = signal<boolean>(false);
  // Snapshot of the PERSISTED access, written once by seedShareState and never
  // touched by the form's own edits. The diff-gate in reconcileShareAccess must
  // read these for "before" — reading the mutable share* signals a second time
  // there would just compare the user's edit to itself (KD-7 regression).
  private readonly shareAccessOriginalOption =
    signal<ShareVisibility>('shared');
  private readonly shareAccessOriginalUserIds = signal<string[]>([]);
  private readonly shareAccessOriginalGroupIds = signal<string[]>([]);
  public readonly ffShareAssistant = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectShareAssistant,
  );
  /** SB-3944: the default chat stays shared with the whole organization. */
  protected readonly isDefaultChat = computed(() =>
    isDefaultChatSkill(this.assistant()),
  );
  /**
   * KD-6 (revised): the kept UI-only flag, re-pointed from forking the
   * create/edit surface to gating Type + Model visibility in this unified
   * builder. `selectFeatureFlag` yields `undefined` (falsy) until LaunchDarkly
   * resolves, so create fails closed to the insight-only basic experience.
   * There is deliberately NO server-side enforcement of this gate (KD-6/R5).
   */
  public readonly ffAdvancedAssistantBuilder =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-advanced-assistant-builder',
      ),
    );
  // One-shot guard for the reactive picker load: the share flag defaults to
  // disabled until LaunchDarkly resolves, so we load when it first turns on and
  // never refetch.
  private shareTargetsLoaded = false;

  /**
   * Per-locale stored (original) description, or `null` when the locale has no
   * baseline (create, or a locale added during this edit). Keyed on the active
   * locale exactly like the DTO the update builder sends, so the grandfather
   * comparison matches the server's stored-value lookup. Single source for both
   * the display ceiling below and the basic form's grandfather validator.
   */
  private storedDescriptionFor(locale: BuilderLocale): string | null {
    const stored = this.assistant()?.translations ?? [];
    // The single-language (`und`) slot grandfathers the skill's one stored
    // translation regardless of the locale it was saved under (und/fr/en). A
    // multilingual skill (>1 stored) has no single baseline for `und`, so it is
    // treated as create-strict when authored fresh in the OFF slot.
    if (locale === 'und') {
      return stored.length === 1 ? (stored[0]?.description ?? null) : null;
    }
    return stored.find((t) => t.locale === locale)?.description ?? null;
  }

  /**
   * Per-locale effective description display ceiling (T5/KD-7, FM-3): the
   * create-strict cap, raised to the stored description's length on edit so a
   * pristine grandfathered value fits its counter, hard-capped by the update
   * maximum. Feeds the field counter / `maxlength` only; the create-strict cap
   * on edited values is validated by `grandfatheredDescriptionMaxLength` keyed
   * on the stored value below. Local function by design: PR-1's final contract
   * ships no shared effective-limit helper.
   */
  private descriptionMaxFor(locale: BuilderLocale): number {
    const stored = this.storedDescriptionFor(locale);
    return Math.min(
      Math.max(DESCRIPTION_MAX_LENGTH, stored?.length ?? 0),
      DESCRIPTION_UPDATE_MAX_LENGTH,
    );
  }

  /**
   * The active UI language, tracked for the single-language (und) compliance
   * slot so its "Customize the upload text" placeholders/defaults resolve in the
   * UI's language rather than a hard-coded locale (KD-21).
   */
  public readonly currentUiLanguage = signal<SupportedLanguage>('en');

  /**
   * SB-3325 PR-4: opt-in multilingual authoring. OFF → one universal locale
   * (persisted as `und` for new skills, or the skill's single stored locale on
   * edit — never rewritten, KD-C); ON → distinct fr + en, both required. Seeded
   * on edit from the stored translation count.
   */
  public readonly supportMultilingual = signal<boolean>(false);
  /**
   * Active authoring slots (KD-18 / spec §6): the single `und` slot when the
   * toggle is OFF, or both fr + en when ON. Only the active slot(s) render,
   * validate, and persist. The three slots are independent FormGroups, so
   * toggling carries nothing between the single-language and multilingual modes.
   */
  public readonly activeLocales = computed<BuilderLocale[]>(() =>
    this.supportMultilingual() ? ['fr', 'en'] : ['und'],
  );

  public readonly descriptionMaxLengthByLocale = computed(() => ({
    und: this.descriptionMaxFor('und'),
    fr: this.descriptionMaxFor('fr'),
    en: this.descriptionMaxFor('en'),
  }));
  public readonly descriptionStoredValueByLocale = computed(() => ({
    und: this.storedDescriptionFor('und'),
    fr: this.storedDescriptionFor('fr'),
    en: this.storedDescriptionFor('en'),
  }));

  // Single locale-aware basic form ref (renders per-locale rows internally).
  public readonly basicFormComponent =
    viewChild<AssistantBuilderBasicFormComponent>('basicForm');

  // Trait form refs (insight/comparison/creative: single form; compliance: per-slot)
  public readonly traitFormComponent =
    viewChild<AssistantBuilderTraitForm>('traitForm');
  public readonly complianceFormUndComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>(
      'complianceFormUnd',
    );
  public readonly complianceFormFrComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>('complianceFormFr');
  public readonly complianceFormEnComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>('complianceFormEn');

  /**
   * The compliance form instance for a builder slot (KD-21 three-slot): `und`
   * is the single-language (OFF) editing surface; `fr`/`en` are the
   * multilingual slots. The three are independent so labels never carry.
   */
  private complianceFormFor(
    locale: BuilderLocale,
  ): AssistantBuilderTraitComplianceFormComponent | undefined {
    switch (locale) {
      case 'en':
        return this.complianceFormEnComponent();
      case 'fr':
        return this.complianceFormFrComponent();
      default:
        return this.complianceFormUndComponent();
    }
  }

  /**
   * Incremented whenever any relevant form's statusChanges emits.
   * Bridges RxJS form observables to signal reactivity for formValidity and
   * invalidLocales computed signals.
   */
  private readonly formStatusVersion = signal(0);

  /**
   * Whether the form can be saved: the selected locale's basic form, every
   * OTHER populated locale's basic form (a locale is populated when it has a
   * non-empty name or description — otherwise its invalid-but-empty state is
   * fine, the DTO simply omits it), and the trait form must all be valid.
   * Gating populated locales closes the silent partial save (A1 Attack-3):
   * previously an invalid EN tab was silently dropped from the payload.
   */
  protected readonly formValidity = computed(() => {
    this.formStatusVersion();

    const basicForm = this.basicFormComponent();
    const active = this.activeLocales();
    // Every active locale's basic form must be valid (OFF → the one primary
    // locale; ON → both fr + en, both required per KD-B). Inactive-locale
    // groups are disabled inside the component, so they never gate Save.
    const basicValid = active.every(
      (locale) => basicForm?.form(locale).valid ?? false,
    );

    let traitValid: boolean;
    // Compare by trait discriminant, not object identity: selectedType() may be
    // set to any TypeOption-shaped object, so `=== TypeOptions.x` is fragile.
    const type = this.selectedType().type;
    if (type === TypeOptions.compliance.type) {
      // Each active slot's compliance form must be valid (OFF → und; ON → fr+en).
      traitValid = active.every(
        (locale) => this.complianceFormFor(locale)?.form().valid ?? false,
      );
    } else if (
      type === TypeOptions.insight.type ||
      type === TypeOptions.generic.type ||
      type === TypeOptions.comparison.type ||
      type === TypeOptions.creative.type
    ) {
      const traitForm = this.traitFormComponent()?.form();
      traitValid = traitForm?.valid ?? false;
    } else {
      traitValid = true;
    }

    return basicValid && traitValid;
  });

  /**
   * Whether the share panel is shown: gated on the share feature flag. The
   * builder is only ever reachable for skills the user owns or is creating
   * (SB-3325 PR-6 removed the read-only, non-owner entry points), so no
   * ownership gate is needed here.
   */
  protected readonly isShareSectionVisible = computed(
    () => this.ffShareAssistant().enabled,
  );

  ngOnInit() {
    this.fileUploadTransactionId.set(uuidv4());
    this.loadModelCatalog();
    this.loadDataboxCatalog();
  }

  /**
   * Load the deployment generation-model catalog for the Model dropdown,
   * mirroring loadShareableTargets(). A failure is captured (not silent) and
   * leaves the catalog empty rather than erroring — null-safe: the dropdown
   * shows no options and create omits `model` (→ deployment default). UI
   * enablement is gated on catalog provisioning (ticket Pre-flight) so users
   * don't see an empty control before the chain is live.
   */
  private loadModelCatalog(): void {
    this.assistantsService
      .assistantsControllerGetAvailableModelsV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((models) => {
        // Map AvailableModelView → ModelOption at this boundary so the component
        // stays decoupled from the generated client (do not feed the generated
        // type straight in — see ModelOption).
        this.availableModels.set(
          models.map((model) => ({
            slug: model.slug,
            displayName: model.displayName,
            isDefault: model.isDefault,
          })),
        );
        this.applyModelSelection();
      });
  }

  /**
   * Pre-select the model: on edit, the skill's persisted slug; otherwise (create,
   * or a pre-epic skill whose stored `model` is null) the catalog default. A
   * stored slug no longer in the catalog still shows until another is picked
   * (the selector falls back to the raw slug).
   *
   * Keep this resolution HERE (run from the catalog `.subscribe` callback, where
   * `assistant()` is already set). Do NOT move it into the `assistant()` form
   * effect in the constructor — that effect can fire before the catalog
   * resolves, so the stored-slug pre-select would run against an empty catalog.
   */
  private applyModelSelection(): void {
    const defaultSlug =
      this.availableModels().find((option) => option.isDefault)?.slug ?? '';
    const storedSlug = this.assistant()?.model ?? null;
    this.selectedModelSlug.set(storedSlug ?? defaultSlug);
    this.initialModelSlug.set(this.selectedModelSlug());
  }

  /**
   * Load the deployment databox catalog for the Databoxes multi-select
   * (SK-117; the API proxies core-ai's GetAvailableDataboxes). Fail-open: on
   * error the list stays empty and `databoxLoadError` drives the inline
   * "unavailable" message, so the rest of the skill still saves (SK-47). The
   * selection is seeded from the stored skill (seedSharedTogglesFromAssistant),
   * NOT here, so a catalog failure cannot clear it.
   */
  private loadDataboxCatalog(): void {
    this.assistantsService
      .assistantsControllerGetAvailableDataboxesV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => {
          this.databoxLoadError.set(true);
          return EMPTY;
        }),
      )
      .subscribe((databoxes) => {
        this.availableDataboxes.set(
          databoxes.map((databox) => ({
            databoxId: databox.databoxId,
            displayName: databox.displayName,
            description: databox.description,
            available: databox.available,
          })),
        );
      });
  }

  /**
   * Type-selector change handler. Mirrors the two-way `selectedType` binding
   * but also clears the databox selection when the type leaves Flex (generic)
   * during create: databoxes are Flex-only and the API refuses them on any
   * other trait (SK-117 / SK-47). The type is locked after save, so this only
   * matters while creating.
   */
  public onSelectedTypeChange(type: TypeOption): void {
    const leavingGeneric =
      this.selectedType().type === TypeOptions.generic.type &&
      type.type !== TypeOptions.generic.type;
    this.selectedType.set(type);
    if (leavingGeneric) {
      this.selectedDataboxIds.set([]);
    }
  }

  /**
   * Seed the shared toggle stack from the stored skill. Called from the
   * once-per-assistant guarded block, so a later re-run of the load effect cannot
   * overwrite a choice the user has since made.
   *
   * `webSearchEnabled` uses `?? null` to preserve "never set" rather than coercing
   * to false — that is what keeps its skill.md fallback intact (SB-3475 KD-3). The
   * other three are plain booleans with no such state.
   *
   * Every line here runs unconditionally on row visibility, deliberately: a value
   * stored for a template that no longer renders its row must still round-trip
   * unchanged rather than be rewritten to the default on the next unrelated save.
   *
   * `webSearchEnabled` used to seed from the model-catalog subscribe callback,
   * which swallows its error: on a catalog failure the signal stayed null while the
   * stored value was true, so `null !== true` made the update send
   * `webSearchEnabled: false` and silently disable search. Seeding all four from
   * `assistant()` here removes that coupling.
   */
  private seedSharedTogglesFromAssistant(
    assistant: fromGenerated.AssistantView,
  ): void {
    this.webSearchEnabled.set(assistant.webSearchEnabled ?? null);
    this.pdfVisualAnalysisEnabled.set(assistant.pdfVisualAnalysisEnabled);
    this.excelFileAnalysisEnabled.set(assistant.excelFileAnalysisEnabled);
    this.disableMessageInput.set(hasRemovePromptInput(assistant.traitConfig));
    // SK-117: seed the databox selection from the stored skill (see
    // initialDataboxIds). assistant.databoxIds is always an array.
    this.selectedDataboxIds.set(assistant.databoxIds ?? []);
    this.initialDataboxIds.set(assistant.databoxIds ?? []);
  }

  constructor() {
    // Track the active UI language for the single-language (und) compliance
    // slot's per-language customize defaults (KD-21).
    this.currentUiLanguage.set(
      (this.translateService.getCurrentLang() ??
        this.translateService.getFallbackLang() ??
        'en') as SupportedLanguage,
    );
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) =>
        this.currentUiLanguage.set(event.lang as SupportedLanguage),
      );

    // Load the people/group pickers the first time the share flag turns on.
    // The flag defaults to disabled until LaunchDarkly resolves, so loading
    // only in `ngOnInit` would miss a late-arriving enable and leave the panel
    // with empty pickers; the guard prevents refetching once populated.
    effect(() => {
      if (this.ffShareAssistant().enabled && !this.shareTargetsLoaded) {
        this.shareTargetsLoaded = true;
        this.loadShareableTargets();
      }
    });

    /**
     * Load assistant into forms when assistant input is set.
     */
    effect(() => {
      const assistant = this.assistant();
      if (!assistant) return;

      this.selectedType.set(
        TypeOptions[
          assistant.trait as
            'insight' | 'generic' | 'comparison' | 'compliance' | 'creative'
        ],
      );

      // Multilingual is ON only when the skill has a real fr + en pair (KD-A).
      // Content-based, not count-based: legacy/partial shapes like [und,fr],
      // [und,en], or [und,fr,en] open OFF (Decision C stale-translation fix) so
      // the rows seed by their actual locale instead of colliding.
      this.supportMultilingual.set(this.hasFrAndEn(assistant));

      // Seed the forms exactly ONCE per loaded assistant, after the relevant
      // view-child form refs resolve. This effect legitimately re-runs (the refs
      // resolve across passes), but it also re-fires after the user edits a
      // form — and re-running the patch would clobber those edits. The visible
      // symptom was edit-mode document removal silently re-seeding: removeFile
      // clears the store, this effect re-patches the trait form from the
      // unchanged assistant, and the file-upload CVA's writeValue re-inserts the
      // document. The selectedType/supportMultilingual sets above are idempotent,
      // so leaving them outside the guard is harmless.
      if (this.patchedAssistantUuid === assistant.uuid) return;
      const traitFormReady =
        assistant.trait === TypeOptions.compliance.type
          ? !!this.complianceFormUndComponent()
          : !!this.traitFormComponent();
      if (!this.basicFormComponent() || !traitFormReady) return;
      this.patchedAssistantUuid = assistant.uuid;

      // Patch the locale-aware basic form
      this.patchBasicFormsFromAssistant(assistant);

      // Patch trait form
      this.patchTraitFormFromAssistant(assistant);

      // Seed the shared toggle stack (web search / PDF visual analysis / Excel
      // file analysis / disable message input) — page-owned signals, not
      // trait-form controls.
      this.seedSharedTogglesFromAssistant(assistant);
    });

    /**
     * Seed the share panel from the skill's current access. Kept in its own
     * effect (not the form-patch one) so resolving `userId` / the share flag
     * only re-reads access — it never re-patches the forms. `onCleanup` cancels
     * an in-flight read before a re-run starts a new one, so a slow response
     * can't land out of order over a newer assistant's access.
     */
    effect((onCleanup) => {
      const sub = this.seedShareState(this.assistant());
      onCleanup(() => sub?.unsubscribe());
    });

    /**
     * Keep shared compliance fields in sync across the und/fr/en slots.
     * Non-translatable fields (removePromptInput, userWillUploadReferenceDocuments,
     * configurationFile, files) are bidirectional so files/toggles/skill.md carry
     * when toggling multilingual; the per-locale customize labels (referenceLabel,
     * regularLabel) are excluded and never carry (KD-21 three-slot no-carry).
     */
    effect((onCleanup) => {
      if (this.selectedType().type !== TypeOptions.compliance.type) return;

      const forms = (['und', 'fr', 'en'] as const)
        .map((locale) => this.complianceFormFor(locale)?.form())
        .filter((form): form is NonNullable<typeof form> => !!form);
      if (forms.length < 2) return;

      const syncFrom = (source: (typeof forms)[number]) => {
        const patch = Object.fromEntries(
          COMPLIANCE_NOT_TRANSLATED_FORM_KEYS.map((formControlKey) => [
            formControlKey,
            source.get(formControlKey)?.value,
          ]),
        );
        for (const target of forms) {
          if (target === source) continue;
          const differs = COMPLIANCE_NOT_TRANSLATED_FORM_KEYS.some(
            (formControlKey) =>
              target.get(formControlKey)?.value !== patch[formControlKey],
          );
          if (differs) {
            const toggleChanged =
              target.controls.userWillUploadReferenceDocuments.value !==
              patch['userWillUploadReferenceDocuments'];
            target.patchValue(patch, { emitEvent: false });
            // SBB-16: each slot component tracks this toggle via valueChanges
            // (toSignal) to disable its reference-label field — a silent mirror
            // would leave the newly-synced slot's disable state stale. Re-emit
            // only the toggle; the differs-guard above stops any sync loop.
            if (toggleChanged) {
              target.controls.userWillUploadReferenceDocuments.setValue(
                patch['userWillUploadReferenceDocuments'] as boolean,
              );
            }
          }
        }
      };

      // Build the subscriptions with loops (not nested map/flatMap) to keep the
      // callback nesting shallow.
      const subs: (Subscription | undefined)[] = [];
      for (const form of forms) {
        for (const formControlKey of COMPLIANCE_NOT_TRANSLATED_FORM_KEYS) {
          subs.push(
            form
              .get(formControlKey)
              ?.valueChanges?.subscribe(() => syncFrom(form)),
          );
        }
      }
      onCleanup(() => subs.forEach((sub) => sub?.unsubscribe()));
    });

    /**
     * Bridge form statusChanges to formStatusVersion signal.
     * Subscribes only to forms that exist for the current selectedType
     * (compliance forms vs trait form).
     */
    effect((onCleanup) => {
      this.activeLocales();
      this.selectedType();

      const basicForm = this.basicFormComponent();
      const undForm = basicForm?.form('und');
      const frForm = basicForm?.form('fr');
      const enForm = basicForm?.form('en');
      const traitForm = this.traitFormComponent()?.form();

      const bump = () => this.formStatusVersion.update((v) => v + 1);
      bump(); // initial

      // Subscribe to all three basic slots (und/fr/en). The `und` slot is the
      // OFF-mode editing surface, so its status must bump the version too or
      // formValidity would go stale when the single-language form changes.
      const subs = [
        undForm?.statusChanges.subscribe(bump),
        frForm?.statusChanges.subscribe(bump),
        enForm?.statusChanges.subscribe(bump),
      ];

      if (this.selectedType().type === TypeOptions.compliance.type) {
        // All three compliance slots (und/fr/en) so validity tracks whichever
        // mode is active.
        subs.push(
          this.complianceFormUndComponent()
            ?.form()
            .statusChanges.subscribe(bump),
          this.complianceFormFrComponent()
            ?.form()
            .statusChanges.subscribe(bump),
          this.complianceFormEnComponent()
            ?.form()
            .statusChanges.subscribe(bump),
        );
      } else {
        subs.push(traitForm?.statusChanges.subscribe(bump));
      }

      onCleanup(() => subs.forEach((s) => s?.unsubscribe()));
    });

    // Compliance shared-store lifecycle (SB-3325 PR-5, lifecycle decision #3):
    // reset both page-owned stores when the trait LEAVES compliance so a
    // compliance → other → compliance round-trip re-enters empty-until-seeded.
    // Entering compliance needs no reset (the stores are already fresh). The
    // multilingual toggle deliberately does NOT reset them (closed-loop
    // invariant #4 — chips survive OFF↔ON).
    let wasCompliance = this.selectedType().type === 'compliance';
    effect(() => {
      const isCompliance = this.selectedType().type === 'compliance';
      if (wasCompliance && !isCompliance) {
        this.complianceConfigStore.reset();
        this.complianceReferenceStore.reset();
      }
      wasCompliance = isCompliance;
    });

    // Belt-and-suspenders on top of the factory's DestroyRef teardown.
    this.destroyRef.onDestroy(() => {
      this.complianceConfigStore.reset();
      this.complianceReferenceStore.reset();
    });
  }

  /**
   * Whether the skill has a real fr + en translation pair — the only shape that
   * opens the multilingual toggle ON (Decision C). Count-based inference would
   * force stale shapes ([und,fr], [und,en], [und,fr,en]) ON and collide rows.
   */
  private hasFrAndEn(assistant: fromGenerated.AssistantView): boolean {
    const locales = new Set(
      (assistant.translations ?? []).map((t) => t.locale),
    );
    return locales.has('fr') && locales.has('en');
  }

  /**
   * The single translation to seed the OFF (und) slot from, by priority
   * und > fr > en (und is canonical; then legacy fr, then en), else the first
   * row. Used for both the basic slot and the compliance labels in OFF mode.
   */
  private pickOffSource(
    translations: fromGenerated.AssistantTranslationView[],
  ): fromGenerated.AssistantTranslationView | undefined {
    return (
      translations.find((t) => t.locale === 'und') ??
      translations.find((t) => t.locale === 'fr') ??
      translations.find((t) => t.locale === 'en') ??
      translations[0]
    );
  }

  /** Patches the locale-aware basic form from assistant translations. */
  private patchBasicFormsFromAssistant(assistant: fromGenerated.AssistantView) {
    const basicForm = this.basicFormComponent();
    if (!basicForm) return;

    // Logo is a single shared control (not per-locale).
    basicForm.logoControl.setValue(assistant.logo, { emitEvent: true });

    // Seed slots by ACTUAL stored locale (Decision C). A real fr+en pair opens
    // ON and seeds the fr/en slots from their own rows (any stray und is
    // ignored). Otherwise the skill opens OFF and seeds the single `und` slot
    // from one deterministic source (und > fr > en); its stored locale is
    // preserved on save via persistLocaleFor.
    const patchSlot = (
      locale: BuilderLocale,
      translation: fromGenerated.AssistantTranslationView | undefined,
    ) => {
      if (!translation) return;
      basicForm.form(locale).patchValue(
        {
          name: translation.name ?? assistant.name,
          description: translation.description ?? assistant.description,
          ...(translation.introMessage
            ? { introMessage: translation.introMessage }
            : {}),
        },
        { emitEvent: true },
      );
    };

    if (this.hasFrAndEn(assistant)) {
      patchSlot(
        'fr',
        assistant.translations.find((t) => t.locale === 'fr'),
      );
      patchSlot(
        'en',
        assistant.translations.find((t) => t.locale === 'en'),
      );
    } else {
      patchSlot('und', this.pickOffSource(assistant.translations));
    }
  }

  /** Maps non-deleted assistant documents to trait form file chip values. */
  private fileChipsFromAssistantDocuments(
    assistant: fromGenerated.AssistantView,
  ): AssistantBuilderFileValue[] {
    return (
      assistant.documents
        ?.filter((doc) => !doc.deletedAt)
        .map((doc) => ({
          id: doc.uuid,
          name: doc.filename,
          extension: doc.extension,
          size: doc.size,
        })) ?? []
    );
  }

  /**
   * uuids of the assistant's live documents carrying the `instruction` role —
   * the skill.md (PR-2 stamps/normalizes it server-side). Drives the
   * role-first edit-mode split between the configuration-file control and the
   * knowledge/reference controls.
   */
  private instructionDocumentUuids(
    assistant: fromGenerated.AssistantView,
  ): Set<string> {
    return new Set(
      (assistant.documents ?? [])
        .filter((doc) => !doc.deletedAt && doc.role === 'instruction')
        .map((doc) => doc.uuid),
    );
  }

  private patchComplianceTraitFormsFromAssistant(
    assistant: fromGenerated.AssistantView,
    files: AssistantBuilderFileValue[],
  ) {
    const traitConfig = assistant.traitConfig;
    if (!isComplianceConfig(traitConfig)) return;

    const undForm = this.complianceFormUndComponent()?.form();
    const frForm = this.complianceFormFrComponent()?.form();
    const enForm = this.complianceFormEnComponent()?.form();
    if (!undForm || !frForm || !enForm) return;

    // Role-first split: instruction → configurationFile, rest → files.
    // Compliance skills exist only new-model (legacy are archived by T8), so
    // the role is always present — no filename fallback needed here.
    const instructionUuids = this.instructionDocumentUuids(assistant);
    const sharedPatch = {
      userWillUploadReferenceDocuments:
        traitConfig.userWillUploadReferenceDocuments,
      configurationFile: files.filter((file) => instructionUuids.has(file.id)),
      files: files.filter((file) => !instructionUuids.has(file.id)),
    };

    // Non-translatable fields are shared → seed all three slots so they carry
    // when the multilingual toggle flips (the three-way sync only fires on later
    // value changes, not on this initial patch).
    undForm.patchValue(sharedPatch, { emitEvent: false });
    frForm.patchValue(sharedPatch, { emitEvent: false });
    enForm.patchValue(sharedPatch, { emitEvent: false });

    // SBB-16: each slot component tracks userWillUploadReferenceDocuments via
    // valueChanges (toSignal) to disable its reference-label field — the silent
    // seed above never reaches it, so an edited skill saved with the toggle ON
    // would open with the field stuck disabled. Re-emit just that control; the
    // three-way sync's differs-guard makes this a no-op mirror-wise.
    for (const form of [undForm, frForm, enForm]) {
      form.controls.userWillUploadReferenceDocuments.setValue(
        sharedPatch.userWillUploadReferenceDocuments,
      );
    }

    // Customize labels seed ONLY the active mode's slot(s) — no carry into the
    // inactive mode (KD-21 three-slot, mirrors patchBasicFormsFromAssistant).
    // Content-based gate (Decision C) so [und,fr]/[und,en]/[und,fr,en] open OFF
    // and seed the und slot from the deterministic OFF source (und > fr > en).
    const labelsFrom = (
      translation: fromGenerated.AssistantTranslationView | undefined,
    ) => {
      const tct =
        translation &&
        isComplianceConfigTranslation(translation.traitConfigTranslation)
          ? translation.traitConfigTranslation
          : undefined;
      return {
        referenceLabel: tct?.labelForExpectedDocuments.referenceLabel ?? '',
        regularLabel: tct?.labelForExpectedDocuments.regularLabel ?? '',
      };
    };

    if (this.hasFrAndEn(assistant)) {
      frForm.patchValue(
        labelsFrom(assistant.translations.find((t) => t.locale === 'fr')),
        { emitEvent: false },
      );
      enForm.patchValue(
        labelsFrom(assistant.translations.find((t) => t.locale === 'en')),
        { emitEvent: false },
      );
    } else {
      undForm.patchValue(
        labelsFrom(this.pickOffSource(assistant.translations)),
        { emitEvent: false },
      );
    }
  }

  /** Patches trait form(s) from assistant; compliance gets per-locale patches. */
  private patchTraitFormFromAssistant(assistant: fromGenerated.AssistantView) {
    const files = this.fileChipsFromAssistantDocuments(assistant);
    const traitForm = this.traitFormComponent()?.form();
    const instructionUuids = this.instructionDocumentUuids(assistant);

    switch (assistant.trait) {
      case 'insight':
        traitForm?.patchValue(
          { prompt: assistant.prompt, files },
          { emitEvent: false },
        );
        break;
      case 'generic': {
        // Role-first split, with the exact-filename match kept as a LEGACY
        // fallback: pre-existing generic rows keep role `regular` until
        // PR-2's backfill normalizes them (KD-15 extended).
        const isConfigurationFile = (file: { id: string; name: string }) =>
          instructionUuids.has(file.id) ||
          file.name === GENERIC_ASSISTANT_CONFIGURATION_FILENAME;
        traitForm?.patchValue(
          {
            sourceFiles: files.filter((file) => !isConfigurationFile(file)),
            configurationFile: files.filter(isConfigurationFile),
          },
          { emitEvent: false },
        );
        break;
      }
      case 'comparison':
        // Dedicated case (T4): the skill.md by role. The behavioral toggle is no
        // longer a control here — `seedSharedTogglesFromAssistant` reads it off
        // traitConfig into the page signal.
        traitForm?.patchValue(
          {
            configurationFile: files.filter((file) =>
              instructionUuids.has(file.id),
            ),
          },
          { emitEvent: false },
        );
        break;
      case 'compliance':
        this.patchComplianceTraitFormsFromAssistant(assistant, files);
        break;
      case 'creative':
        traitForm?.patchValue({ files }, { emitEvent: false });
        break;
      default:
        // Reserved traits (research / wizard / extract) are not creatable or
        // editable here — `selectedType` has no TypeOptions entry for them — so
        // this branch is unreachable in practice. It used to blind-spread
        // traitConfig into the trait form, which no longer typechecks now that
        // `removePromptInput` is a page-level signal rather than a form control on
        // any trait form: there is no key left for a raw traitConfig to patch.
        // Left as an explicit no-op rather than a cast that would silently
        // reintroduce a second writer for removePromptInput.
        break;
    }
  }

  /**
   * Single source of truth for disabling Save and for bailing out of the save handler.
   */
  public isSaveButtonDisabled(): boolean {
    // Upload state now gates Save through ordinary form validity: each zone
    // registers NG_VALIDATORS ({uploadPending}/{uploadFailed}/{uploadInvalid})
    // on its control, so a pending/failed/invalid upload invalidates the trait
    // form cross-slot (SB-3325 PR-5). The old global `selectAreFilesLoading`
    // gate is gone.
    return !this.formValidity() || this.isSaving();
  }

  /**
   * Defense-in-depth for the compliance shared-store path (SB-3325 PR-5): the
   * disabled button + form validity are the primary gate, but re-check the two
   * page-owned stores directly so a save fails loud rather than shipping an
   * in-flight/invalid/failed file (never a silent done-only DTO filter, which
   * would drop the file with no signal).
   */
  private hasBlockingComplianceUpload(): boolean {
    if (this.selectedType().type !== TypeOptions.compliance.type) return false;
    const isBlocking = (store: FileUploadV2Store): boolean =>
      store
        .getFiles()
        .some(
          (file) =>
            file.status === 'upload-pending' ||
            file.status === 'upload-ongoing' ||
            file.status === 'upload-invalid' ||
            file.status === 'upload-failed',
        );
    return (
      isBlocking(this.complianceConfigStore) ||
      isBlocking(this.complianceReferenceStore)
    );
  }

  public async handleClickSaveCTA() {
    if (this.isSaveButtonDisabled() || this.hasBlockingComplianceUpload()) {
      return;
    }
    this.isSaving.set(true);
    const assistant = this.assistant();
    try {
      // The skill the reconcile below shares, and whose identity `skill_shared`
      // reports. On create this must be the create *response* — the `assistant`
      // input signal is still undefined until the parent rebinds it after
      // `loadAssistants()`, long after the event needs to fire.
      let skillForReconcile: fromGenerated.AssistantView;
      if (assistant) {
        const dto = this.buildUpdateAssistantDto(assistant);
        const updated = await firstValueFrom(
          this.routeMode() === 'admin'
            ? this.adminService.adminManagedSkillsControllerUpdateV1(
                assistant.uuid,
                dto,
              )
            : this.assistantsService.assistantsControllerUpdateAssistantV1(
                assistant.uuid,
                dto,
              ),
        );
        // Deliberately the loaded `assistant`, not `updated`: this keeps the
        // shipped edit-path `skill_shared` payload byte-identical. (It means the
        // event describes the skill as it was loaded — pre-existing behavior,
        // out of scope here.)
        skillForReconcile = assistant;
        this.analyticsService
          .sendSkillUpdatedEvent(fromHelper.toSkillSavedEvent(updated))
          .subscribe();
      } else {
        const created = await firstValueFrom(
          this.assistantsService.assistantsControllerCreateAssistantV1(
            this.buildCreateAssistantDto(),
          ),
        );
        skillForReconcile = created;
        this.analyticsService
          .sendSkillCreatedEvent(fromHelper.toSkillSavedEvent(created))
          .subscribe();
        // Apply the favorite staged in the New state (D6 / spec §5).
        if (this.stagedFavorite()) {
          this.assistantsStore.dispatch(
            fromFavorites.AssistantFavoritesActions.addFavorite({
              assistantId: created.uuid,
              assistant: created,
            }),
          );
        }
      }
      // Reconcile sharing after the skill is persisted (needs its uuid). A
      // sharing failure is surfaced but must not fail the (saved) skill.
      await this.reconcileShareAccess(skillForReconcile);
      this.assistantsStore.dispatch(AssistantsActions.loadAssistants());
      // The same words on every route, as the skill delete toast: the admin
      // page's keys. A share failure has its own toast; the skill is saved.
      this.toastrService.success(
        this.translateService.instant(
          assistant
            ? 'ADMIN.MANAGED_SKILLS.TOAST.UPDATE_SUCCESS'
            : 'ADMIN.MANAGED_SKILLS.TOAST.CREATE_SUCCESS',
        ) as string,
        undefined,
        { closeButton: true },
      );
      // Create and edit both close the modal on save (mirrors the teammate
      // builder); the list reloads above.
      this.closeBuilder();
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_error) {
      // The same words as the success toast's sibling keys; every create,
      // update and delete error stays until it is closed.
      this.toastrService.error(
        this.translateService.instant(
          assistant
            ? 'ADMIN.MANAGED_SKILLS.TOAST.UPDATE_ERROR'
            : 'ADMIN.MANAGED_SKILLS.TOAST.CREATE_ERROR',
        ) as string,
        undefined,
        { closeButton: true, disableTimeOut: true },
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * sd-modal footer config (SB-3325 PR-4, Stage B). No chrome title/close — the
   * builder renders its own in-body header (title + favorite star), so the
   * dialog's accessible name is supplied via `ariaLabel` (the skill name, or the
   * create title). Delete (edit only) is pulled to the far left via
   * separateDestructiveAction, Cancel + Save/Create sit on the right. Capped at
   * 1512 px via `maxWidth` so the surface stays centered with a horizontal
   * gutter below the cap (never full-bleed), 80 px margins (D1).
   */
  protected modalOptions(): fromSeriousSystem.Modals {
    const actions: fromSeriousSystem.ModalAction[] = [];
    if (this.assistant()) {
      actions.push({
        label: this.translateService.instant(
          'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE',
        ) as string,
        action: 'delete',
        type: 'delete-text',
        leadingIcon: { type: 'uiBasic', icon: 'delete' },
        disabled: this.isSaving(),
      });
    }
    actions.push({
      label: this.translateService.instant(
        'ASSISTANTS.ASSISTANT_BUILDER.CTA_CANCEL',
      ) as string,
      action: 'cancel',
      type: 'cancel',
      disabled: this.isSaving(),
    });
    actions.push({
      label: this.translateService.instant(
        this.assistant()
          ? 'ASSISTANTS.ASSISTANT_BUILDER.CTA_SAVE'
          : 'ASSISTANTS.ASSISTANT_BUILDER.CTA_CREATE',
      ) as string,
      action: 'save',
      type: 'save',
      disabled: this.isSaveButtonDisabled(),
    });
    return {
      title: '',
      ariaLabel:
        this.assistant()?.name ??
        (this.translateService.instant(
          'ASSISTANTS.ASSISTANT_BUILDER.TITLE_CREATE',
        ) as string),
      maxWidth: 1512,
      showCloseButton: false,
      showFooterSeparator: true,
      backdropDismiss: !this.isSaving(),
      separateDestructiveAction: true,
      actions,
    };
  }

  /** sd-modal action router: save / delete / (cancel|backdrop|escape → close). */
  protected onModalAction(action: string): void {
    switch (action) {
      case 'save':
        void this.handleClickSaveCTA();
        break;
      case 'delete':
        this.handleClickDeleteCTA();
        break;
      default:
        this.handleClickCancelCTA();
    }
  }

  /** Close the builder modal (dispatches the store close action). */
  private closeBuilder(): void {
    this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
  }

  /**
   * Whether the builder holds unsaved authoring work — drives the discard
   * confirm on cancel/backdrop/escape. Keys on the reactive forms' dirty state:
   * loading an existing skill patches without marking dirty, so only genuine
   * user edits count.
   */
  public hasChanges(): boolean {
    const basicForm = this.basicFormComponent();
    const basicDirty = this.activeLocales().some(
      (locale) => basicForm?.form(locale).dirty ?? false,
    );
    const logoDirty = basicForm?.logoControl.dirty ?? false;
    const traitDirty =
      this.selectedType().type === TypeOptions.compliance.type
        ? !!this.complianceFormUndComponent()?.form().dirty ||
          !!this.complianceFormFrComponent()?.form().dirty ||
          !!this.complianceFormEnComponent()?.form().dirty
        : !!this.traitFormComponent()?.form().dirty;
    return basicDirty || logoDirty || traitDirty;
  }

  public handleClickCancelCTA() {
    if (!this.hasChanges()) {
      this.closeBuilder();
      return;
    }
    this.confirmDialog
      .confirm({
        title: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.TITLE',
        ) as string,
        message: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.DESCRIPTION',
        ) as string,
        confirmLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_DISCARD',
        ) as string,
        cancelLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_CANCEL',
        ) as string,
        confirmType: 'delete',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.closeBuilder();
        }
      });
  }

  /** Footer Delete (edit only) — opens the confirm modal (Figma 25309 footer). */
  public readonly isConfirmDeleteModalOpen = signal(false);

  public handleClickDeleteCTA(): void {
    if (!this.assistant()) return;
    this.isConfirmDeleteModalOpen.set(true);
  }

  /**
   * Confirm-modal action handler: on `delete`, dispatch the existing delete
   * action (effect calls the API + removes it from the store) and close the
   * builder modal, consistent with save/cancel; `cancel` just closes the
   * confirmation.
   */
  public handleConfirmDeleteModalAction(action: string): void {
    this.isConfirmDeleteModalOpen.set(false);
    if ((action as fromSeriousSystem.ModalAction['type']) !== 'delete') return;
    const skill = this.assistant();
    if (!skill) return;
    if (this.routeMode() === 'admin') {
      void this.deleteManagedSkill(skill);
      return;
    }
    this.assistantsStore.dispatch(
      AssistantActions.deleteAssistant({ uuid: skill.uuid, assistant: skill }),
    );
    this.closeBuilder();
  }

  /**
   * Admin delete waits for the response. A 404 leaves the builder open.
   * Success reuses the owner success action so the entity map,
   * `skill_deleted` and the success toast stay on the one path. The failure
   * toast uses the admin page's words, as every other skill delete does.
   */
  private async deleteManagedSkill(
    skill: fromGenerated.AssistantView,
  ): Promise<void> {
    this.isSaving.set(true);
    try {
      await firstValueFrom(
        this.adminService
          .adminManagedSkillsControllerDeleteV1(skill.uuid)
          .pipe(sentryCaptureError),
      );
      this.assistantsStore.dispatch(
        AssistantActions.deleteAssistantSuccess({
          uuid: skill.uuid,
          assistant: skill,
        }),
      );
      this.closeBuilder();
    } catch (error) {
      this.toastrService.error(
        this.translateService.instant(
          defaultChatLockKey(readApiErrorName(error)) ??
            'ADMIN.MANAGED_SKILLS.TOAST.DELETE_ERROR',
        ) as string,
        undefined,
        { closeButton: true, disableTimeOut: true },
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Load the users and groups the invite picker offers. Tied to the component
   * lifetime so an in-flight request can't outlive the page; a failure is
   * captured (not silent) and leaves the picker empty rather than erroring.
   */
  private loadShareableTargets(): void {
    // A super_admin's summary list includes other super_admins
    // (`getHiddenRolesForUserList` returns []). The summary has no role, so
    // the picker reads the admin user list and drops them: super_admins stay
    // invisible to clients, so the share picker never offers one and no
    // grant is ever created in a super_admin's name. An id already in the
    // share set stays in `shareSelectedUserIds`. `directoryUsers` keeps them,
    // for the Owner row only (`shareOwner`).
    const users$ =
      this.userId()?.role === 'super_admin'
        ? this.adminService.adminControllerGetUsersV1().pipe(
            map((users) => {
              const directory = users.map((user) => ({
                id: user.id,
                email: user.email,
                fullName: user.fullName,
                initials: user.initials,
              }));
              const superAdminIds = new Set(
                users
                  .filter((user) => user.role === 'super_admin')
                  .map((user) => user.id),
              );
              return {
                directory,
                pickable: directory.filter(
                  (user) => !superAdminIds.has(user.id),
                ),
              };
            }),
          )
        : this.usersService
            .usersControllerGetUsersV1()
            .pipe(map((users) => ({ directory: users, pickable: users })));
    users$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe(({ directory, pickable }) => {
        this.directoryUsers.set(directory);
        this.allUsers.set(pickable);
      });
    this.groupsService
      .groupsControllerGetGroupsV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((groups) => this.allGroups.set(groups));
  }

  /**
   * The Owner row of the share panel. In admin mode, the skill's real owner,
   * looked up from its `ownedBy` in the unfiltered admin user list: Article IV
   * (v1.6.0) lets a `super_admin` caller see who owns a `super_admin`'s skill,
   * and no skill route carries that name. Otherwise the caller, on a new skill
   * or their own one.
   */
  public readonly shareOwner = computed(() => {
    const ownerId =
      this.routeMode() === 'admin'
        ? this.assistant()?.ownedBy
        : this.userId()?.id;
    if (!ownerId) return null;
    return this.directoryUsers().find((user) => user.id === ownerId) ?? null;
  });

  /**
   * Seed the share dropdown from the skill's current access when editing an
   * owned skill; reset to private otherwise. Maps the access graph back to the
   * three dropdown states (org-wide → public, any direct user/group grant →
   * shared, else private). Group grants are seeded into the picker (visible,
   * removable rows) and re-sent on reconcile: sharing is owner-authoritative
   * replace semantics, not passthrough.
   *
   * Returns the in-flight access read so the calling effect can cancel it via
   * `onCleanup` before a re-run. If the read fails, `shareAccessLoaded` is left
   * `false` so the reconcile leaves the access graph untouched (the skill still
   * saves) — deliberately NOT reset-to-private, which a save would then read as
   * "revoke everything".
   */
  private seedShareState(
    assistant: fromGenerated.AssistantView | undefined,
  ): Subscription | void {
    const currentUserId = this.userId()?.id;
    const shareEnabled = this.ffShareAssistant().enabled;
    const isAdmin = this.routeMode() === 'admin';
    const isOwnedEditable =
      !isAdmin &&
      !!assistant &&
      !!currentUserId &&
      assistant.ownedBy === currentUserId &&
      shareEnabled;
    const isAdminEditable = isAdmin && !!assistant && shareEnabled;

    if (!assistant || (!isOwnedEditable && !isAdminEditable)) {
      // New skill (nothing to read) or non-owned owner mode (panel hidden):
      // owner-only "Only people invited" (no grants) is the correct,
      // fully-known desired state, so the reconcile may proceed.
      // Admin mode with the share flag off lands here too: the panel is
      // hidden, so reconcile returns before any PUT.
      this.shareOption.set('shared');
      this.shareSelectedUserIds.set([]);
      this.shareSelectedGroupIds.set([]);
      this.shareAccessOriginalOption.set('shared');
      this.shareAccessOriginalUserIds.set([]);
      this.shareAccessOriginalGroupIds.set([]);
      this.shareAccessLoaded.set(true);
      return;
    }

    this.shareAccessLoaded.set(false);
    const access$ = isAdminEditable
      ? this.adminService.adminManagedSkillsControllerGetAccessV1(
          assistant.uuid,
        )
      : this.assistantsService.assistantsControllerGetAssistantAccessV1(
          assistant.uuid,
        );
    return access$
      .pipe(
        // Capture (not silent) and swallow: on failure `next` never fires, so
        // `shareAccessLoaded` stays false and the reconcile leaves access
        // untouched.
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((access) => this.applyShareSeed(access));
  }

  private applyShareSeed(access: fromGenerated.AssistantAccessView): void {
    this.shareSelectedUserIds.set(access.users.map((user) => user.id));
    this.shareSelectedGroupIds.set(access.groups.map((group) => group.id));
    const seededOption = accessToShareOption(
      access.orgWide,
      access.users.length,
      access.groups.length,
    );
    this.shareOption.set(seededOption);
    // Frozen snapshot for the KD-7 diff-gate (reconcileShareAccess) — the
    // share* signals above are mutated by the user's own edits from here
    // on, so they can no longer answer "what was persisted before?".
    this.shareAccessOriginalOption.set(seededOption);
    this.shareAccessOriginalUserIds.set(access.users.map((user) => user.id));
    this.shareAccessOriginalGroupIds.set(
      access.groups.map((group) => group.id),
    );
    this.shareAccessLoaded.set(true);
  }

  /**
   * Reconcile the skill's share state after it has been saved. Best-effort: the
   * skill is already persisted, so a sharing failure is shown as a toast but
   * does not fail the save. No-op when the share panel is not shown. Maps the
   * dropdown: public → org-wide, shared → the picked users + groups, private →
   * neither. Sharing is owner-authoritative replace semantics: the full desired
   * set is sent and any grant not in it is removed (no passthrough).
   *
   * Fires skill_shared event (diff-gated per KD-7) only if the share set actually
   * changed. Payload reflects FINAL STATE (not delta) with sharedWithType + count.
   */
  private async reconcileShareAccess(
    skill: fromGenerated.AssistantView,
  ): Promise<void> {
    const uuid = skill.uuid;
    // Skip when the panel isn't shown, or when current access hasn't loaded —
    // reconciling the empty defaults would silently revoke existing grants.
    if (!this.isShareSectionVisible() || !this.shareAccessLoaded()) {
      return;
    }

    // Admin mode skips the PUT when the share controls are unchanged. A
    // description-only save must not replace the set. `public` hides direct
    // grants, so an unchanged public option is not a share edit even when the
    // seed still holds user and group ids. The PUT replaces the whole share
    // set, which is why an unchanged panel must not send it. Owner mode still
    // PUTs on every save.
    if (this.routeMode() === 'admin' && !this.shareControlsDifferFromSeed()) {
      return;
    }

    // BEFORE state for the diff-gate comes from the frozen snapshot seedShareState
    // took at load time — NOT the mutable share* signals, which by now already
    // hold the user's edit and would just compare it to itself (KD-7 regression,
    // confirmed live: an owner-only → org-wide switch never fired skill_shared).
    const beforeUserIds = this.shareAccessOriginalUserIds();
    const beforeGroupIds = this.shareAccessOriginalGroupIds();
    const beforeOption = this.shareAccessOriginalOption();

    const isShared = this.shareOption() === 'shared';
    const payload = {
      userIds: isShared ? this.shareSelectedUserIds() : [],
      groupIds: isShared ? this.shareSelectedGroupIds() : [],
      orgWide: this.shareOption() === 'public',
    };
    try {
      await firstValueFrom(
        this.routeMode() === 'admin'
          ? this.adminService.adminManagedSkillsControllerSetAccessV1(
              uuid,
              payload,
            )
          : this.assistantsService.assistantsControllerSetAssistantAccessV1(
              uuid,
              payload,
            ),
      );

      // Compute diff: detect real changes (added/removed users/groups, orgWide change)
      const afterUserIds = isShared ? this.shareSelectedUserIds() : [];
      const afterGroupIds = isShared ? this.shareSelectedGroupIds() : [];
      const afterOrgWide = this.shareOption() === 'public';
      const beforeOrgWide = beforeOption === 'public';

      const hasDiff = this.hasShareAccessChanged(
        {
          userIds: beforeUserIds,
          groupIds: beforeGroupIds,
          orgWide: beforeOrgWide,
        },
        {
          userIds: afterUserIds,
          groupIds: afterGroupIds,
          orgWide: afterOrgWide,
        },
      );

      // Fire skill_shared event only on real diff (KD-7: diff-gate)
      if (hasDiff) {
        // Derived from the `skill` argument, never from the `assistant` input
        // signal: on create that signal is still undefined here, which silently
        // dropped the event on every create-with-sharing.
        const assistantForAnalytics = this.toAnalyticsAssistant(skill);
        if (assistantForAnalytics) {
          this.analyticsService
            .sendSkillSharedEvent({
              assistant: assistantForAnalytics,
              // KD-3: skill_shared carries teammate/accessPath/group like the
              // other skill lifecycle events. No gating anywhere on the skill
              // side — KD-18(b)'s skill_created conditional was deleted, and
              // KD-18(a) only ever applied to teammate_created. Every key is
              // conditionally spread inside the helper, so nothing arrives as
              // an explicit undefined (exactOptionalPropertyTypes).
              //
              // Derived from `skill`, never from the `assistant` input signal:
              // that signal is still undefined here on create, which is what
              // silently dropped this event on every create-with-sharing (P0 #3).
              ...fromHelper.toSkillAnalyticsContext(skill),
              // Counts what the panel shows (#1707). The PUT above and the
              // diff-gate keep every id, so a hidden grant still survives Save.
              ...this.buildSkillSharedPayload(
                this.visibleShareUserIds(afterUserIds),
                this.visibleShareGroupIds(afterGroupIds),
                afterOrgWide,
              ),
            })
            .subscribe();
        }
      }
    } catch (error) {
      this.toastrService.error(
        this.translateService.instant(
          defaultChatLockKey(readApiErrorName(error)) ??
            'ASSISTANTS.SHARE.SAVE_ERROR',
        ) as string,
        undefined,
        { closeButton: true, disableTimeOut: true },
      );
    }
  }

  /**
   * True when the user changed the share controls since the seed. An unchanged
   * `public` option does not count the hidden direct grants as a change.
   */
  private shareControlsDifferFromSeed(): boolean {
    if (this.shareOption() !== this.shareAccessOriginalOption()) return true;
    if (this.shareOption() === 'public') return false;
    return this.hasShareAccessChanged(
      {
        userIds: this.shareAccessOriginalUserIds(),
        groupIds: this.shareAccessOriginalGroupIds(),
        orgWide: false,
      },
      {
        userIds: this.shareSelectedUserIds(),
        groupIds: this.shareSelectedGroupIds(),
        orgWide: false,
      },
    );
  }

  /** True if the before/after share sets differ (users, groups, or org-wide). Helper for `reconcileShareAccess`'s KD-7 diff-gate. */
  private hasShareAccessChanged(
    before: { userIds: string[]; groupIds: string[]; orgWide: boolean },
    after: { userIds: string[]; groupIds: string[]; orgWide: boolean },
  ): boolean {
    const userIdsBefore = new Set(before.userIds);
    const userIdsAfter = new Set(after.userIds);
    const groupIdsBefore = new Set(before.groupIds);
    const groupIdsAfter = new Set(after.groupIds);

    const usersChanged =
      userIdsBefore.size !== userIdsAfter.size ||
      Array.from(userIdsBefore).some((id) => !userIdsAfter.has(id));
    const groupsChanged =
      groupIdsBefore.size !== groupIdsAfter.size ||
      Array.from(groupIdsBefore).some((id) => !groupIdsAfter.has(id));
    const orgWideChanged = before.orgWide !== after.orgWide;

    return usersChanged || groupsChanged || orgWideChanged;
  }

  /**
   * The user ids the share panel renders: those present in the picker's user
   * list. That list hides `super_admin` users from client callers (SB-3048,
   * `getHiddenRolesForUserList`), yet the access seed still carries their
   * grants. Until the list loads, or if it fails, no user id is visible — the
   * panel renders none either. Helper for `reconcileShareAccess`.
   */
  private visibleShareUserIds(userIds: string[]): string[] {
    const visible = new Set(this.allUsers().map((user) => user.id));
    return userIds.filter((id) => visible.has(id));
  }

  /**
   * The group ids the share panel renders: those present in the picker's group
   * list. Both that list and the access seed hold only custom groups, so they
   * differ only while the list loads or after it fails — the panel then renders
   * no group, and none is counted. Helper for `reconcileShareAccess`.
   */
  private visibleShareGroupIds(groupIds: string[]): string[] {
    const visible = new Set(this.allGroups().map((group) => group.id));
    return groupIds.filter((id) => visible.has(id));
  }

  /** Compute the `skill_shared` payload's sharedWithType/sharedWithCount from final share state. Helper for `reconcileShareAccess`. */
  private buildSkillSharedPayload(
    afterUserIds: string[],
    afterGroupIds: string[],
    afterOrgWide: boolean,
  ): { sharedWithType: SharedWithType; sharedWithCount: number } {
    // org-wide counts as 1 aggregate
    const sharedWithCount = afterOrgWide
      ? 1
      : afterUserIds.length + afterGroupIds.length;

    let sharedWithType: SharedWithType;
    if (afterOrgWide) {
      sharedWithType = 'array'; // org-wide is array type
    } else if (afterUserIds.length > 0 && afterGroupIds.length === 0) {
      sharedWithType = 'user';
    } else if (afterUserIds.length === 0 && afterGroupIds.length > 0) {
      sharedWithType = 'group';
    } else if (afterUserIds.length === 0 && afterGroupIds.length === 0) {
      // Revoking the last share still fires (KD-7 diff-gate); `array` would
      // have made this indistinguishable from a genuine mix.
      sharedWithType = 'none';
    } else {
      sharedWithType = 'array'; // mixed
    }

    return { sharedWithType, sharedWithCount };
  }

  // ---------------------------------------------------------------------------
  // DTO builders — read directly from form values, no intermediate cache
  // ---------------------------------------------------------------------------

  private getBasicFormValues(
    locale: BuilderLocale,
  ): AssistantBuilderBasicFormValues {
    return (
      this.basicFormComponent()?.getValues(locale) ?? {
        name: '',
        description: '',
        introMessage: '',
        logo: NEW_SKILL_DEFAULT_LOGO,
      }
    );
  }

  /**
   * Raw trait form values. For compliance, pass locale for per-locale labels.
   */
  private getTraitFormValues(
    locale?: BuilderLocale,
  ):
    | AssistantBuilderTraitComparisonFormValues
    | AssistantBuilderTraitComplianceFormValues
    | AssistantBuilderTraitGenericFormValues
    | Record<string, unknown> {
    if (this.selectedType().type === TypeOptions.compliance.type) {
      const slot = locale ?? this.activeLocales()[0] ?? 'und';
      const form = this.complianceFormFor(slot)?.form();
      return form?.getRawValue() ?? {};
    }
    return this.traitFormComponent()?.form().getRawValue() ?? {};
  }

  private buildTranslation(locale: BuilderLocale) {
    const v = this.getBasicFormValues(locale);
    const traitValues = this.getTraitFormValues(locale);
    let traitConfigTranslation: object = {};

    if (this.selectedType().type === TypeOptions.compliance.type) {
      const cv = traitValues as AssistantBuilderTraitComplianceFormValues;
      // KD-21/D15: the customize-text labels are OPTIONAL and decoupled from the
      // reference toggle. A blank field persists the per-language default so the
      // usage-side CTA still renders sensible text; a custom value persists
      // verbatim. Materialize-on-save keeps the stored shape unchanged.
      const defaultFor = (key: string): string => {
        const translations = this.translateStore.getTranslations(locale);
        const fromStore = translations
          ? (getValue(translations, key) as string | undefined)
          : undefined;
        return fromStore ?? (this.translateService.instant(key) as string);
      };
      const labelOrDefault = (
        value: string | undefined,
        key: string,
      ): string => (value?.trim() ? value : defaultFor(key));

      traitConfigTranslation = {
        labelForExpectedDocuments: {
          referenceLabel: labelOrDefault(
            cv.referenceLabel,
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REFERENCE_LABEL.DEFAULT',
          ),
          regularLabel: labelOrDefault(
            cv.regularLabel,
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REGULAR_LABEL.DEFAULT',
          ),
        },
      };
    }

    return {
      // The persisted locale (KD-A/C): fr/en when multilingual, else `und` for a
      // new/universal skill or the skill's existing single stored locale.
      locale: this.persistLocaleFor(locale),
      name: v.name,
      description: v.description,
      // Required (non-empty) on create since PR-2 (T5) — the create-mode
      // validators enforce it before Save enables; the update builder
      // overrides it with null when blank (grandfathered optional intro).
      introMessage: v.introMessage ?? '',
      traitConfigTranslation,
    };
  }

  /**
   * Files to persist, tagged with their document role (T4/KD-15): the
   * skill.md configuration file is `instruction` for ALL skill.md traits —
   * generic included — compliance knowledge files are `reference`, and
   * everything else (insight files, generic sourceFiles) is `regular`.
   * The UI stamp is belt-and-suspenders payload clarity only: PR-2's server
   * re-derives `instruction` from the skill.md authoritatively and rejects a
   * client-supplied `instruction` on any other document.
   */
  private getFiles(): RoleTaggedFile[] {
    // Non-translatable fields (files/configurationFile) are shared across the
    // compliance slots, so read from the primary active slot (und OFF, fr ON).
    const traitValues = this.getTraitFormValues();
    const type = this.selectedType().type;
    const tagFiles = (
      files: { id: string; name: string }[] | undefined,
      role: fromGenerated.DocumentRole,
    ): RoleTaggedFile[] =>
      (files ?? []).map((file) => ({ id: file.id, name: file.name, role }));

    const taggedFiles: RoleTaggedFile[] = [];

    // These casts narrow `getTraitFormValues()`' four-way union, which is the same
    // thing lines above do. What they deliberately no longer do is re-spell the
    // element shape: `{ id: string; name: string }` used to be written out here
    // because `GenericAssistantFileLike` declared only `name`, so the real type
    // could not be named without losing `id` — the very field this method reads.
    // Now that it carries `id`, each cast names the real type and follows it.
    if (type === 'generic') {
      const genericValues =
        traitValues as Partial<AssistantBuilderTraitGenericFormValues>;
      taggedFiles.push(...tagFiles(genericValues.sourceFiles, 'regular'));
    }
    if (type === 'compliance') {
      const complianceValues =
        traitValues as Partial<AssistantBuilderTraitComplianceFormValues>;
      taggedFiles.push(...tagFiles(complianceValues.files, 'reference'));
    }
    if (type === 'insight' || type === 'creative') {
      const fileValues = traitValues as { files?: AssistantBuilderFileValue[] };
      taggedFiles.push(...tagFiles(fileValues.files, 'regular'));
    }
    if ((SKILL_MD_TRAITS as readonly string[]).includes(type)) {
      // Generic, comparison and compliance each declare `configurationFile` with
      // this element type, so the field is named rather than the owning form.
      const configurationValues = traitValues as {
        configurationFile?: AssistantBuilderTraitGenericFilesValue;
      };
      taggedFiles.push(
        ...tagFiles(configurationValues.configurationFile, 'instruction'),
      );
    }

    return taggedFiles;
  }

  /** Whether basic + compliance (if applicable) forms are valid for the slot. */
  private isLocaleValid(locale: BuilderLocale): boolean {
    const basicValid = this.basicFormComponent()?.form(locale).valid;
    const complianceForm = this.complianceFormFor(locale)?.form();
    const complianceValid =
      this.selectedType().type !== TypeOptions.compliance.type ||
      complianceForm?.valid;
    return !!(basicValid && complianceValid);
  }

  /**
   * Builds API traitConfig — behavioral-only (T4/KD-2): the instructions now
   * live in the skill.md, so compliance keeps its reference-documents toggle
   * (labels are per-locale translations) and nothing else survives per-trait.
   *
   * `removePromptInput` comes from the page-owned `disableMessageInput` signal
   * for EVERY template that has it (all but Basic), not from a per-trait form.
   * That single writer is the point: the key used to be a FormControl on the
   * comparison and compliance forms, and leaving those in place while adding the
   * shared row would give one persisted key two owners.
   */
  private buildTraitConfig(): object {
    if (this.selectedType().type === TypeOptions.insight.type) return {};

    const removePromptInput = this.disableMessageInput();

    if (this.selectedType().type === TypeOptions.compliance.type) {
      const cv =
        this.getTraitFormValues() as AssistantBuilderTraitComplianceFormValues;
      return {
        removePromptInput,
        userWillUploadReferenceDocuments: cv.userWillUploadReferenceDocuments,
      };
    }

    // generic (Flex), comparison (Compare Documents) and creative (Content
    // Generation) all carry exactly this one key.
    return { removePromptInput };
  }

  /**
   * The locale each active UI locale (fr/en) is PERSISTED under (KD-A/C / R1):
   * multilingual ON → itself; OFF → the skill's existing single stored locale
   * (legacy fr/en never gratuitously rewritten) or `und` for a new/universal
   * skill. Drives both the translation rows and the update's localesToDelete.
   */
  private persistLocaleFor(uiLocale: BuilderLocale): 'fr' | 'en' | 'und' {
    if (this.supportMultilingual()) return uiLocale;
    const stored = this.assistant()?.translations ?? [];
    if (stored.length === 1 && stored[0]) {
      const storedLocale = stored[0].locale;
      if (
        storedLocale === 'fr' ||
        storedLocale === 'en' ||
        storedLocale === 'und'
      ) {
        return storedLocale;
      }
    }
    return 'und';
  }

  private buildCreateAssistantDto(): fromGenerated.CreateAssistantDto {
    // Logo is shared across slots; read it from the primary active slot (`und`
    // when OFF, `fr` when ON).
    const primaryValues = this.getBasicFormValues(
      this.activeLocales()[0] ?? 'und',
    );

    const translations = this.activeLocales()
      .filter((uiLocale) => this.isLocaleValid(uiLocale))
      .map((uiLocale) => this.buildTranslation(uiLocale))
      .filter((t) => isValidTranslation(t));

    const insightValues = this.getTraitFormValues() as { prompt?: string };
    const prompt =
      this.selectedType().type === TypeOptions.insight.type
        ? (insightValues.prompt ?? '')
        : '';

    const files = this.getFiles();

    return {
      translations,
      logo: primaryValues.logo,
      ...(prompt ? { prompt } : {}),
      trait: this.selectedType().type,
      // Persisted at create; editable later through the update DTO (SB-4074).
      // Omitted when empty (catalog not loaded) → null → deployment default.
      // Also omitted when the Model control was never shown (flag-off create,
      // KD-6): the basic experience sends no `model`, like the legacy modal.
      ...(this.ffAdvancedAssistantBuilder() && this.selectedModelSlug()
        ? { model: this.selectedModelSlug() }
        : {}),
      // SK-117: Flex (generic) trait only; the API refuses databoxes on any
      // other trait. Omit when empty or when the row was never shown.
      ...(this.ffAdvancedAssistantBuilder() &&
      this.selectedType().type === TypeOptions.generic.type &&
      this.selectedDataboxIds().length > 0
        ? { databoxIds: this.selectedDataboxIds() }
        : {}),
      // SB-3475: omitted unless this super_admin actually set it. `null` means
      // "no opinion" and must stay absent so the skill defers to its skill.md —
      // sending `false` here would override that (Scenario A1 / A7).
      //
      // The availability check is NOT symmetry, and it is the same two-sided rule
      // #1435 Decision 1 established for the Excel row. The Type selector is
      // editable for the whole create window, nothing resets this signal when the
      // template changes, and the API now guards the TRAIT axis:
      // `validateWebSearchEnabledForTrait` 400s `true` on the four traits with no
      // builder template (SB-3537 / KD-28, #1479). So a creator who turns the row on for
      // Flex and then picks Basic would have their save rejected outright, naming
      // a field no longer on screen. The UI must not send what the server refuses,
      // on a template that never offered the row.
      ...(this.showWebSearchToggle() && this.webSearchEnabled() !== null
        ? { webSearchEnabled: this.webSearchEnabled() === true }
        : {}),
      // Omit unless ON. `false` is the server-side default, so sending it would
      // add nothing — and it would 403 for the non-super_admins who never saw the
      // toggle, since the API rejects the field rather than ignoring it.
      // Same availability check, same reason as `webSearchEnabled` above:
      // `validatePdfVisualAnalysisEnabledForTrait` 400s `true` on the four
      // traits with no builder template (SB-3537 / KD-28, #1479).
      ...(this.showPdfVisualAnalysisToggle() && this.pdfVisualAnalysisEnabled()
        ? { pdfVisualAnalysisEnabled: true }
        : {}),
      // Omit unless ON. `false` is the server-side default, so sending it adds
      // nothing — and unlike `pdfVisualAnalysisEnabled` above there is no ROLE
      // validator to 403 here (SB-3537 / KD-6).
      //
      // The availability check is NOT symmetry. The Type selector is editable
      // for the whole create window, nothing resets this signal when the
      // template changes, and the API guards the TRAIT axis:
      // `validateExcelFileAnalysisEnabledForTrait` 400s `true` on `insight`. So
      // a creator who turns the row on and then picks Basic would have their
      // save rejected outright without it (#1435 plan / Decision 1). Same
      // two-sided shape as `removePromptInput`, whose UI half is
      // `buildTraitConfig()` returning {} on insight: the UI must not send what
      // the server refuses, on a template that never offered the row.
      ...(this.showExcelFileAnalysisToggle() && this.excelFileAnalysisEnabled()
        ? { excelFileAnalysisEnabled: true }
        : {}),
      traitConfig: this.buildTraitConfig(),
      transactionId: this.fileUploadTransactionId(),
      // Per-file role from the tagged getFiles() result (T4/KD-15): the
      // skill.md is `instruction` for all three skill.md traits; the server
      // re-derives it authoritatively.
      documentsToAdd: files.map((file) => ({
        documentId: file.id,
        filename: file.name,
        role: file.role,
      })),
    };
  }

  /**
   * Order-independent equality of two databox-id lists (SK-117 change
   * detection: the update DTO sends `databoxIds` only on a real change).
   */
  private haveSameDataboxIds(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const set = new Set(a);
    return b.every((id) => set.has(id));
  }

  private buildUpdateAssistantDto(
    assistant: fromGenerated.AssistantView,
  ): fromGenerated.UpdateAssistantDto {
    // Logo is shared across slots; read it from the primary active slot.
    const primaryValues = this.getBasicFormValues(
      this.activeLocales()[0] ?? 'und',
    );

    const existingDocuments =
      assistant.documents?.filter((doc) => !doc.deletedAt) ?? [];
    const files = this.getFiles();

    const documentIdsToDelete = existingDocuments
      .filter((doc) => !files.some((file) => file.id === doc.uuid))
      .map((doc) => doc.uuid);

    // Per-file role from the tagged getFiles() result (T4/KD-15); add/delete
    // diffing keys on document uuid, unchanged.
    const documentsToAdd = files
      .filter((file) => !existingDocuments.some((doc) => doc.uuid === file.id))
      .map((file) => ({
        documentId: file.id,
        filename: file.name,
        role: file.role,
      }));

    const translations = this.activeLocales()
      .filter((uiLocale) => this.isLocaleValid(uiLocale))
      .map((uiLocale) => {
        const basic = this.getBasicFormValues(uiLocale);
        return {
          ...this.buildTranslation(uiLocale),
          introMessage: (basic.introMessage ?? '').trim()
            ? basic.introMessage
            : null,
        };
      })
      .filter((t) => isValidTranslation(t));

    // localesToDelete (SB-3325 PR-4 / R1): stored locales no longer targeted are
    // deleted so a mode change (und↔fr/en) can't leave stale rows shadowing the
    // new content (the update path is otherwise upsert-only). Target set = the
    // persisted locales of the active UI locales — see the target-set table.
    const targetLocales = new Set<string>(
      this.activeLocales().map((uiLocale) => this.persistLocaleFor(uiLocale)),
    );
    const localesToDelete = (assistant.translations ?? [])
      .map((translation) => translation.locale)
      .filter(
        (locale) => !targetLocales.has(locale),
      ) as fromGenerated.UpdateAssistantDto.LocalesToDeleteEnum[];

    const insightValues = this.getTraitFormValues() as { prompt?: string };
    const prompt =
      this.selectedType().type === TypeOptions.insight.type
        ? (insightValues.prompt ?? '')
        : '';

    return {
      translations,
      logo: primaryValues.logo,
      ...(prompt ? { prompt } : {}),
      traitConfig: this.buildTraitConfig(),
      transactionId: this.fileUploadTransactionId(),
      ...(documentsToAdd.length > 0 ? { documentsToAdd } : {}),
      ...(documentIdsToDelete.length > 0 ? { documentIdsToDelete } : {}),
      ...(localesToDelete.length > 0 ? { localesToDelete } : {}),
      // SB-3475 (Scenario A7): send ONLY on a real change. A user who edits the
      // description of a search-enabled skill, or who never saw the toggle at all
      // (non-super_admin, or a trait where it is not rendered), must not have the
      // stored value silently rewritten — and a non-super_admin sending the field
      // would be 403'd by the API even when the value is unchanged.
      ...(this.webSearchEnabled() !== (assistant.webSearchEnabled ?? null)
        ? { webSearchEnabled: this.webSearchEnabled() === true }
        : {}),
      // Send ONLY on a real change. This is what a plain boolean has to express
      // explicitly where the tri-state got it for free: a non-super_admin never
      // saw this toggle, and the API 403s the field even when the value is
      // unchanged — so an unrelated edit must not carry it.
      ...(this.pdfVisualAnalysisEnabled() !== assistant.pdfVisualAnalysisEnabled
        ? { pdfVisualAnalysisEnabled: this.pdfVisualAnalysisEnabled() }
        : {}),
      // Send ONLY on a real change. There is no role gate to trip here
      // (SB-3537 / KD-6), so the reason is the stored value itself: a creator
      // who only edits a description must not have their toggle state rewritten
      // by an unrelated save.
      //
      // No availability check on this path — `trait` is immutable on edit and
      // seeding runs unconditionally, so an untouched row compares equal and is
      // omitted. Which is what protects a value the builder cannot show: an
      // explicit `false` stored on a Basic skill through a direct API call,
      // which the validator accepts on purpose so an enabled skill stays
      // switchable off. A `true` there is not reachable — the column, its
      // migration and the trait guard all ship in the same PR (#1470) — but the
      // unconditional seed is also what a "don't seed what isn't shown"
      // refactor would break, and that is the case the tests pin.
      ...(this.excelFileAnalysisEnabled() !== assistant.excelFileAnalysisEnabled
        ? { excelFileAnalysisEnabled: this.excelFileAnalysisEnabled() }
        : {}),
      // SB-4074: send ONLY on a real change. Never when the row is hidden (flag
      // off) or the catalog failed to load (empty slug), and never the value
      // pre-selected at load, so an untouched null-model skill stays null.
      ...(this.ffAdvancedAssistantBuilder() &&
      this.selectedModelSlug() &&
      this.selectedModelSlug() !== this.initialModelSlug()
        ? { model: this.selectedModelSlug() }
        : {}),
      // SK-117: send ONLY on a real change vs the stored selection (order-
      // independent). An empty array clears it server-side. `trait` is
      // immutable on edit, so a non-Flex skill keeps an empty selection that
      // compares equal and is omitted.
      ...(this.ffAdvancedAssistantBuilder() &&
      !this.haveSameDataboxIds(
        this.selectedDataboxIds(),
        this.initialDataboxIds(),
      )
        ? { databoxIds: this.selectedDataboxIds() }
        : {}),
    };
  }
}
